import React, { useState, useEffect, useRef, startTransition } from 'react';
import '../styles/TaskBoard.css';
import TaskDescriptionEditor from '../components/TaskDescriptionEditor';
import TaskCalendarView, { CalendarTask } from '../components/TaskCalendarView';
import { ReactComponent as CardViewIcon } from '../icons/taskboard_cardview.svg';
import { ReactComponent as ListViewIcon } from '../icons/taskboard_listview.svg';
import { ReactComponent as GoogleDriveIcon } from '../icons/taskboard_googledrive.svg';
import { ReactComponent as FigmaIcon } from '../icons/taskboard_figma.svg';
import { ReactComponent as JiraIcon } from '../icons/taskboard_jira.svg';
import { ReactComponent as AttachIcon } from '../icons/taskboard_attach.svg';
import { ReactComponent as ImageIcon } from '../icons/taskboard_image.svg';
import { ReactComponent as TagsIcon } from '../icons/taskboard_tags.svg';
import { ReactComponent as URLIcon } from '../icons/taskboard_url.svg';
import { ReactComponent as ReminderIcon } from '../icons/taskboard_reminder.svg';
import { ReactComponent as GoogleDocsIcon } from '../icons/taskboard_googledocs.svg';
import { ReactComponent as PriorityLowestIcon } from '../icons/priority_lowest.svg';
import { ReactComponent as PriorityLowIcon } from '../icons/priority_low.svg';
import { ReactComponent as PriorityMediumIcon } from '../icons/priority_medium.svg';
import { ReactComponent as PriorityHighIcon } from '../icons/priority_high.svg';
import { ReactComponent as PriorityHighestIcon } from '../icons/priority_highest.svg';
import { ReactComponent as NotesIcon } from '../icons/taskboard_notes.svg';
import { SavedTaskBoard, Column, Task, SubTask, Comment, taskboardService } from '../services/taskboardService';
import { organizationService } from '../services/organizationService';
import { orgStore } from '../lib/orgStore';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

interface TaskBoardProps {
  taskboard: SavedTaskBoard | null;
  onBackToList: () => void;
  onUpdate: (taskboard: SavedTaskBoard) => void;
  taskToOpen?: { taskId: string; columnId: string } | null;
  onTaskOpened?: () => void;
}

const TaskBoard: React.FC<TaskBoardProps> = ({ taskboard, onBackToList, onUpdate, taskToOpen, onTaskOpened }) => {
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  // Confirmation state for moving task to a "done" column
  const [pendingDrop, setPendingDrop] = useState<{ task: Task; sourceColumnId: string; targetColumnId: string; dropIndex?: number } | null>(null);

  const [viewMode, setViewMode] = useState<'board' | 'list' | 'calendar'>('board');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState<string | null>(null);
  const [showUrlField, setShowUrlField] = useState(false);
  const [showDriveField, setShowDriveField] = useState(false);
  const [showFigmaField, setShowFigmaField] = useState(false);
  const [showJiraField, setShowJiraField] = useState(false);
  const [showImageField, setShowImageField] = useState(false);
  const [showTagsField, setShowTagsField] = useState(false);
  const [showNotesField, setShowNotesField] = useState(false);
  const [showDocsField, setShowDocsField] = useState(false);
  const [showSubtasksField, setShowSubtasksField] = useState(false);

  const [showReminderField, setShowReminderField] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showTagsDropdown, setShowTagsDropdown] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [columns, setColumns] = useState<Column[]>([]);
  const [taskboardName, setTaskboardName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [draggedTask, setDraggedTask] = useState<{ task: Task; sourceColumnId: string } | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const [dragOverTaskId, setDragOverTaskId] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<{ taskId: string; columnId: string } | null>(null);
  const [createAnother, setCreateAnother] = useState(false);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<'priority' | 'assignee' | 'labels' | 'status' | null>(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

  const handleAttachmentUpload = async (file: File) => {
    try {
      setIsUploadingAttachment(true);
      const orgId = orgStore.getOrgId() || 'default_org';
      const taskId = editingTask?.taskId || 'new_task_' + Math.random().toString(36).substring(2, 9);
      const fileName = `${orgId}/${taskId}/${Date.now()}_${file.name}`;
      
      const { data, error } = await supabase.storage
        .from('attachments')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        throw error;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('attachments')
        .getPublicUrl(fileName);

      setNewTaskData(prev => ({ ...prev, image: publicUrl }));
    } catch (err) {
      console.warn('Supabase storage upload failed, falling back to base64 reader:', err);
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewTaskData(prev => ({ ...prev, image: event.target?.result as string }));
      };
      reader.readAsDataURL(file);
    } finally {
      setIsUploadingAttachment(false);
    }
  };
  const [filters, setFilters] = useState({
    tags: [] as string[],
    priority: [] as string[],
    dateFrom: '',
    dateTo: ''
  });
  const [sortConfig, setSortConfig] = useState<{
    field: 'title' | 'priority' | 'created' | null;
    direction: 'asc' | 'desc' | null;
  }>({
    field: null,
    direction: null
  });
  const [newTaskData, setNewTaskData] = useState({
    title: '',
    description: '',
    url: [] as string[],
    drive: [] as string[],
    figma: [] as string[],
    jira: [] as string[],
    docs: [] as string[],
    image: '',
    tags: [] as string[],
    priority: 'medium' as 'lowest' | 'low' | 'medium' | 'high' | 'highest',
    notes: '',
    subtasks: [] as SubTask[],
    reminder: '',
    project: 'Engineering Board',
    assignee: 'Unassigned',
    startDate: '',
    sprint: '',
    storyPoints: '',
    reporter: 'Akash Sharma',
    status: '',
    dueDate: ''
  });
  const [currentTag, setCurrentTag] = useState('');
  const [currentUrl, setCurrentUrl] = useState('');
  const [currentDrive, setCurrentDrive] = useState('');
  const [currentFigma, setCurrentFigma] = useState('');
  const [currentJira, setCurrentJira] = useState('');
  const [currentDocs, setCurrentDocs] = useState('');
  const [currentSubtask, setCurrentSubtask] = useState('');

  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  // Comment + history state
  const [taskComments, setTaskComments] = useState<Comment[]>([]);
  const [commentInput, setCommentInput] = useState('');
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [orgMembers, setOrgMembers] = useState<{ id: string; name: string }[]>([]);
  const [commentAuthorId, setCommentAuthorId] = useState('');
  const [commentAuthorName, setCommentAuthorName] = useState('');
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [commentTab, setCommentTab] = useState<'comments' | 'history'>('comments');
  const [taskHistory, setTaskHistory] = useState<{ id: string; action: string; user: string; time: string }[]>([]);
  // Persist history per task so it survives re-opening the same task
  const allTaskHistoryRef = useRef<Record<string, { id: string; action: string; user: string; time: string }[]>>({});
  const originalTaskRef = useRef<typeof newTaskData | null>(null);

  const addHistoryEntry = (action: string, taskId?: string) => {
    const entry = {
      id: `h-${Date.now()}`,
      action,
      user: commentAuthorName || 'You',
      time: new Date().toISOString(),
    };
    const id = taskId || editingTask?.taskId || '';
    if (id) {
      allTaskHistoryRef.current[id] = [entry, ...(allTaskHistoryRef.current[id] || [])];
    }
    setTaskHistory(prev => [entry, ...prev]);
  };

  const buildHistoryEntries = (prev: typeof newTaskData, next: typeof newTaskData): string[] => {
    const entries: string[] = [];
    if (prev.title !== next.title)
      entries.push(`Title changed from "${prev.title}" to "${next.title}"`);
    if (prev.status !== next.status)
      entries.push(`Status changed from "${prev.status || 'none'}" to "${next.status || 'none'}"`);
    if (prev.priority !== next.priority)
      entries.push(`Priority changed from "${prev.priority}" to "${next.priority}"`);
    if (prev.assignee !== next.assignee)
      entries.push(`Assignee changed from "${prev.assignee}" to "${next.assignee}"`);
    const prevDesc = getDescriptionText(prev.description);
    const nextDesc = getDescriptionText(next.description);
    if (prevDesc !== nextDesc)
      entries.push(`Description updated`);
    if (JSON.stringify(prev.tags) !== JSON.stringify(next.tags))
      entries.push(`Tags updated`);
    if (prev.dueDate !== next.dueDate)
      entries.push(`Due date changed from "${prev.dueDate || 'none'}" to "${next.dueDate || 'none'}"`);
    if (prev.notes !== next.notes)
      entries.push(`Notes updated`);
    return entries;
  };

  // Refs for click outside detection
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const tagsDropdownRef = useRef<HTMLDivElement>(null);
  const addFormRef = useRef<HTMLDivElement>(null);
  const editFormRef = useRef<HTMLDivElement>(null);
  const boardScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showJumpMenu, setShowJumpMenu] = useState(false);

  // Rich text editor refs (one per form instance)
  const addDescRef  = useRef<HTMLDivElement>(null);
  const editDescRef = useRef<HTMLDivElement>(null);

  // List view column definitions — order + widths, persisted to localStorage
  const LIST_COLS_DEFAULT = ['summary', 'tags', 'roadmap', 'subtasks', 'comments', 'actions'] as const;
  type ListColKey = typeof LIST_COLS_DEFAULT[number];
  const DEFAULT_WIDTHS: Record<ListColKey, number> = { summary: 0, tags: 160, roadmap: 145, subtasks: 105, comments: 105, actions: 68 };
  const lsKey = `list-cols-${taskboard?.id || 'default'}`;
  const [listColOrder, setListColOrder] = useState<ListColKey[]>(() => {
    try { const s = localStorage.getItem(lsKey + '-order'); return s ? JSON.parse(s) : [...LIST_COLS_DEFAULT]; } catch { return [...LIST_COLS_DEFAULT]; }
  });
  const [listColWidths, setListColWidths] = useState<Record<ListColKey, number>>(() => {
    try { const s = localStorage.getItem(lsKey + '-widths'); return s ? { ...DEFAULT_WIDTHS, ...JSON.parse(s) } : { ...DEFAULT_WIDTHS }; } catch { return { ...DEFAULT_WIDTHS }; }
  });
  const colResizeRef = useRef<{ col: ListColKey; startX: number; startW: number } | null>(null);
  const colDragRef = useRef<{ col: ListColKey } | null>(null);
  const [colDragOver, setColDragOver] = useState<ListColKey | null>(null);

  // Stable refs so save callbacks never capture stale closure values
  const taskboardRef          = useRef(taskboard);
  const columnsRef            = useRef(columns);
  const taskboardNameRef      = useRef(taskboardName);
  const taskboardUpdatedAtRef = useRef(taskboard?.updated_at ?? '');
  useEffect(() => { taskboardRef.current          = taskboard;                   }, [taskboard]);
  useEffect(() => { columnsRef.current            = columns;                     }, [columns]);
  // Update scroll arrows whenever columns change
  useEffect(() => { setTimeout(updateScrollButtons, 100); }, [columns]);
  useEffect(() => { taskboardNameRef.current      = taskboardName;               }, [taskboardName]);
  useEffect(() => { taskboardUpdatedAtRef.current = taskboard?.updated_at ?? ''; }, [taskboard]);

  // Extract plain text from description (supports editablejs JSON + legacy HTML)
  const getDescriptionText = (desc: string): string => {
    if (!desc) return '';

    const extractText = (n: any): string => {
      if (typeof n === 'string') return n;
      if (typeof n.text === 'string') return n.text;
      if (Array.isArray(n.children)) return n.children.map(extractText).join(' ');
      return '';
    };

    const extractFromNodes = (nodes: any): string => {
      if (Array.isArray(nodes)) return nodes.map(extractText).join(' ').replace(/\s+/g, ' ').trim();
      if (nodes && typeof nodes === 'object') {
        if (Array.isArray(nodes.children)) return extractFromNodes(nodes.children);
      }
      return '';
    };

    // Format 1: current — "__editablejs__:[...json array...]"
    const SENTINEL = '__editablejs__:';
    if (desc.startsWith(SENTINEL)) {
      try { return extractFromNodes(JSON.parse(desc.slice(SENTINEL.length))); } catch { /* fall through */ }
    }

    // Format 2: legacy object wrapper — {"__editablejs__":[...]} or {"__editablejs__":true,...}
    if (desc.startsWith('{')) {
      try {
        const parsed = JSON.parse(desc);
        // {"__editablejs__": [...nodes...]}
        if (Array.isArray(parsed.__editablejs__)) return extractFromNodes(parsed.__editablejs__);
        // {"__editablejs__": true, "type": "paragraph", "children": [...]}
        if (parsed.__editablejs__ && parsed.type) return extractFromNodes([parsed]);
        // Any JSON with children/text at root
        if (parsed.type || parsed.children || parsed.text) return extractFromNodes([parsed]);
      } catch { /* fall through */ }
    }

    // Format 3: plain HTML — strip tags
    return desc.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  };

  // Save queue — ensures only one save is in-flight at a time.
  // If a save arrives while one is running, we keep only the latest pending
  // save (coalescing) so rapid actions never send stale versions.
  const saveInFlightRef = useRef(false);
  const pendingSaveRef  = useRef<null | { columns: Column[]; name: string }>(null);

  const enqueueSave = (cols: Column[], name: string) => {
    if (saveInFlightRef.current) {
      // Coalesce — overwrite any previous pending save with the latest state
      pendingSaveRef.current = { columns: cols, name };
      return;
    }
    const board = taskboardRef.current;
    if (!board) return;
    saveInFlightRef.current = true;
    taskboardService.updateTaskBoard(
      board.id,
      { name, columns: cols },
      taskboardUpdatedAtRef.current
    ).then(saved => {
      syncUpdatedAt(saved);
      saveInFlightRef.current = false;
      // Flush any pending save that arrived while we were in-flight
      if (pendingSaveRef.current) {
        const next = pendingSaveRef.current;
        pendingSaveRef.current = null;
        enqueueSave(next.columns, next.name);
      }
    }).catch(err => {
      saveInFlightRef.current = false;
      pendingSaveRef.current = null;
      // Direct-Supabase save is last-write-wins; any error here is a real failure
      // (auth / RLS / network) — surface it instead of swallowing it silently.
      console.error('Error in enqueueSave:', err);
      alert(`Failed to save task board: ${err?.message ?? err}`);
    });
  };

  // After every successful save we must update the version ref so the next save
  // uses the new updated_at, not the stale one from the initial load.
  const syncUpdatedAt = (board: { updated_at: string }) => {
    taskboardUpdatedAtRef.current = board.updated_at;
  };

  // ── Rich text editor helpers ──────────────────────────────────────────────
  // Seed content into the editor div (only on first open — called in useEffect)
  const seedDescription = (ref: React.RefObject<HTMLDivElement>, html: string) => {
    if (ref.current && ref.current.getAttribute('data-seeded') !== '1') {
      // content comes from our own DB — safe to use as editor value
      ref.current.innerHTML = html || ''; // eslint-disable-line
      ref.current.setAttribute('data-seeded', '1');
    }
  };

  // Saved selection range — used to restore cursor after prompt() steals focus
  const savedRangeRef = useRef<Range | null>(null);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreSelection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (sel && savedRangeRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
  };

  // Execute a rich-text formatting command, keeping focus in the editor
  const execRichCmd = (
    ref: React.RefObject<HTMLDivElement>,
    cmd: string,
    value?: string
  ) => {
    ref.current?.focus();
    document.execCommand(cmd, false, value ?? undefined); // eslint-disable-line
  };

  // Handle toolbar button mousedown — prevent blur, run command, sync state
  const onToolbarBtn = (
    e: React.MouseEvent,
    ref: React.RefObject<HTMLDivElement>,
    cmd: string,
    value?: string
  ) => {
    e.preventDefault(); // stop blur so selection is preserved
    if (cmd === 'createLink') {
      // prompt() steals focus — save selection first, restore after
      saveSelection();
      const url = prompt('Enter URL:');
      if (!url) return;
      restoreSelection(ref);
      document.execCommand('createLink', false, url); // eslint-disable-line
    } else {
      execRichCmd(ref, cmd, value);
    }
    setTimeout(() => {
      if (ref.current) {
        setNewTaskData(prev => ({ ...prev, description: ref.current!.innerHTML })); // eslint-disable-line
      }
    }, 0);
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ── List view column resize handlers ─────────────────────────────────────
  const startColResize = (e: React.MouseEvent, col: ListColKey) => {
    e.preventDefault();
    e.stopPropagation();
    colResizeRef.current = { col, startX: e.clientX, startW: listColWidths[col] };
    const onMove = (ev: MouseEvent) => {
      if (!colResizeRef.current) return;
      const { col: c, startX, startW } = colResizeRef.current;
      const newW = Math.max(60, startW + ev.clientX - startX);
      setListColWidths(prev => {
        const next = { ...prev, [c]: newW };
        localStorage.setItem(lsKey + '-widths', JSON.stringify(next));
        return next;
      });
    };
    const onUp = () => { colResizeRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const startColDrag = (col: ListColKey) => { colDragRef.current = { col }; };
  const onColDragOver = (e: React.DragEvent, col: ListColKey) => { e.preventDefault(); setColDragOver(col); };
  const onColDrop = (targetCol: ListColKey) => {
    if (!colDragRef.current || colDragRef.current.col === targetCol) { setColDragOver(null); return; }
    const src = colDragRef.current.col;
    setListColOrder(prev => {
      const next = [...prev];
      const si = next.indexOf(src); const ti = next.indexOf(targetCol);
      if (si < 0 || ti < 0) return prev;
      next.splice(si, 1); next.splice(ti, 0, src);
      localStorage.setItem(lsKey + '-order', JSON.stringify(next));
      return next;
    });
    colDragRef.current = null; setColDragOver(null);
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Seed edit-form editor when editingTask opens, and clear data-seeded on close
  useEffect(() => {
    if (editingTask) {
      if (editDescRef.current) editDescRef.current.removeAttribute('data-seeded');
      setTimeout(() => seedDescription(editDescRef, newTaskData.description), 0);
    }
  }, [editingTask]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset add-form editor when add form opens
  useEffect(() => {
    if (showAddForm) {
      if (addDescRef.current) {
        addDescRef.current.removeAttribute('data-seeded');
        addDescRef.current.innerHTML = ''; // eslint-disable-line
      }
    }
  }, [showAddForm]);

  // Sync columns from prop ONLY on initial load (when local state is empty).
  // After that, local state is the source of truth — onUpdate() round-trips
  // should never overwrite local edits the user is actively making.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (taskboard && !initializedRef.current) {
      initializedRef.current = true;
      setColumns(taskboard.columns);
      setTaskboardName(taskboard.name);
    }
  }, [taskboard]);

  // Auto-open task card view from URL parameters (e.g., from notification click)
  useEffect(() => {
    if (taskToOpen && columns.length > 0 && !editingTask) {
      const { taskId, columnId } = taskToOpen;

      // Find the task in the specified column
      const column = columns.find(col => col.id === columnId);
      const task = column?.tasks.find(t => t.id === taskId);

      if (task) {
        // Open the card view for this task using handleEditTask
        handleEditTask(columnId, taskId);
        // Clear taskToOpen to prevent reopening after form is closed
        if (onTaskOpened) {
          onTaskOpened();
        }
      }
    }
  }, [taskToOpen, columns]);

  // Click outside detection for filter and sort dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (filterDropdownRef.current && !filterDropdownRef.current.contains(target)) {
        setShowFilterDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(target)) {
        setShowSortDropdown(false);
      }
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(target)) {
        setShowTagsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-scroll for add form
  useEffect(() => {
    if (showAddForm && addFormRef.current) {
      setTimeout(() => {
        addFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [showAddForm]);

  // (auto-scroll removed â€" edit form is now a fixed modal, always in viewport)

  // Load org members + current user for comments
  useEffect(() => {
    const load = async () => {
      const orgId = orgStore.getOrgId();
      if (!orgId) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setCommentAuthorId(session.user.id);
        setCommentAuthorName(session.user.user_metadata?.full_name || session.user.email || 'Me');
      }
      try {
        const members = await organizationService.getMembersWithProfiles(orgId);
        setOrgMembers(
          members
            .filter(m => m.is_active && (m.full_name || m.email))
            .map(m => ({ id: m.user_id, name: m.full_name || m.email || '' }))
        );
      } catch (e) {
        console.error('Error loading org members for mentions:', e);
      }
    };
    load();
  }, []);

  // Auto-save functionality — reads from refs so it never captures stale values
  useEffect(() => {
    if (!taskboardRef.current) return;

    const timer = setTimeout(() => {
      handleSave();
    }, 2000);

    return () => clearTimeout(timer);
  }, [columns, taskboardName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reminder notification system
  useEffect(() => {
    const checkReminders = () => {
      const now = new Date().getTime();

      columns.forEach(column => {
        column.tasks.forEach(task => {
          if (task.reminder) {
            const reminderTime = new Date(task.reminder).getTime();
            const timeDiff = reminderTime - now;

            // Show notification if reminder is within 1 minute or overdue
            if (timeDiff <= 60000 && timeDiff > -60000) {
              // Request permission if not granted
              if (Notification.permission === 'granted') {
                new Notification('Task Reminder', {
                  body: `${task.title}${task.description ? '\n' + task.description : ''}`,
                  icon: '/favicon.ico',
                  tag: task.id, // Prevent duplicate notifications
                });
              } else if (Notification.permission !== 'denied') {
                Notification.requestPermission().then(permission => {
                  if (permission === 'granted') {
                    new Notification('Task Reminder', {
                      body: `${task.title}${task.description ? '\n' + task.description : ''}`,
                      icon: '/favicon.ico',
                      tag: task.id,
                    });
                  }
                });
              }
            }
          }
        });
      });
    };

    // Check reminders every 30 seconds
    const interval = setInterval(checkReminders, 30000);
    // Also check immediately
    checkReminders();

    return () => clearInterval(interval);
  }, [columns]);

  const handleSave = async () => {
    const board = taskboardRef.current;
    if (!board) return;

    try {
      setIsSaving(true);
      const saved = await taskboardService.updateTaskBoard(
        board.id,
        { name: taskboardNameRef.current, columns: columnsRef.current },
        taskboardUpdatedAtRef.current
      );
      syncUpdatedAt(saved);
      // Do NOT call onUpdate — it would re-trigger setColumns and overwrite local state
    } catch (error: any) {
      if (error?.status === 409) {
        console.warn('Save conflict — board was modified elsewhere. Re-fetching...');
        // Re-fetch the latest version so the next save has the correct version token
        if (taskboardRef.current) {
          const latest = await taskboardService.getTaskBoard(taskboardRef.current.id);
          if (latest) syncUpdatedAt(latest);
        }
        return;
      }
      console.error('Error saving taskboard:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTask = (columnId: string) => {
    // Title is the only required field — description (and everything else) is optional
    // so a task always saves on click.
    if (!newTaskData.title.trim()) {
      alert('Title is required');
      return;
    }

    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) + ' ' + now.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toUpperCase();

    const targetColumnId = newTaskData.status || columnId;

    const newTask: Task = {
      id: Date.now().toString(),
      title: newTaskData.title,
      description: newTaskData.description,
      tags: newTaskData.tags,
      status: targetColumnId,
      priority: newTaskData.priority,
      image: newTaskData.image,
      url: newTaskData.url,
      drive: newTaskData.drive,
      figma: newTaskData.figma,
      jira: newTaskData.jira,
      docs: newTaskData.docs,
      notes: newTaskData.notes,
      subtasks: newTaskData.subtasks,
      createdAt: formattedDate,
      reminder: newTaskData.reminder || undefined,
      project: newTaskData.project,
      assignee: newTaskData.assignee,
      startDate: newTaskData.startDate,
      sprint: newTaskData.sprint,
      storyPoints: newTaskData.storyPoints,
      reporter: newTaskData.reporter,
      dueDate: newTaskData.dueDate || undefined
    };

    console.log('Adding task to column:', targetColumnId, newTask);

    const updatedColumns = columns.map(col => {
      if (col.id === targetColumnId) {
        const updatedCol = { ...col, tasks: [...col.tasks, newTask] };
        console.log('Updated column:', updatedCol);
        return updatedCol;
      }
      return col;
    });

    console.log('All columns after update:', updatedColumns);
    setColumns(updatedColumns);
    // Instant save — use refs to avoid stale closure
    const board = taskboardRef.current;
    if (board) {
      enqueueSave(updatedColumns, taskboardNameRef.current);
    }

    if (createAnother) {
      // Clear title and description, keep other metadata and keep the popup open!
      setNewTaskData(prev => ({
        ...prev,
        title: '',
        description: '',
        subtasks: []
      }));
      setCurrentSubtask('');
    } else {
      setNewTaskData({
        title: '',
        description: '',
        url: [],
        drive: [],
        figma: [],
        jira: [],
        docs: [],
        image: '',
        tags: [],
        priority: 'medium' as 'lowest' | 'low' | 'medium' | 'high' | 'highest',
        notes: '',
        subtasks: [],
        reminder: '',
        project: 'Engineering Board',
        assignee: 'Unassigned',
        startDate: '',
        sprint: '',
        storyPoints: '',
        reporter: 'Akash Sharma',
        status: '',
        dueDate: ''
      });
      setCurrentTag('');
      setCurrentUrl('');
      setCurrentDrive('');
      setCurrentFigma('');
      setCurrentJira('');
      setCurrentDocs('');
      setCurrentSubtask('');
      setShowAddForm(null);
      setShowUrlField(false);
      setShowDriveField(false);
      setShowFigmaField(false);
      setShowJiraField(false);
      setShowDocsField(false);
      setShowImageField(false);
      setShowTagsField(false);
      setShowNotesField(false);
      setShowSubtasksField(false);
      setShowReminderField(false);
    }
  };

  const handleCancelAddTask = () => {
    setShowAddForm(null);
    setEditingTask(null);
    setTaskComments([]);
    setCommentInput('');
    setShowMentionDropdown(false);
    setNewTaskData({
      title: '',
      description: '',
      url: [],
      drive: [],
      figma: [],
      jira: [],
      docs: [],
      image: '',
      tags: [],
      priority: 'medium' as 'lowest' | 'low' | 'medium' | 'high' | 'highest',
      notes: '',
      subtasks: [],
      reminder: '',
      project: 'Engineering Board',
      assignee: 'Unassigned',
      startDate: '',
      sprint: '',
      storyPoints: '',
      reporter: 'Akash Sharma',
      status: '',
      dueDate: ''
    });
    setCurrentTag('');
    setCurrentUrl('');
    setCurrentDrive('');
    setCurrentFigma('');
    setCurrentJira('');
    setCurrentDocs('');
    setCurrentSubtask('');
    setActiveDropdown(null);
    setShowUrlField(false);
    setShowDriveField(false);
    setShowFigmaField(false);
    setShowJiraField(false);
    setShowDocsField(false);
    setShowImageField(false);
    setShowTagsField(false);
    setShowNotesField(false);
    setShowSubtasksField(false);
    setShowReminderField(false);
  };

  const handleEditTask = (columnId: string, taskId: string) => {
    setCommentTab('comments');
    // Load persisted history for this task (or init with an "opened" entry if first time)
    const existingHistory = allTaskHistoryRef.current[taskId];
    if (existingHistory && existingHistory.length > 0) {
      setTaskHistory(existingHistory);
    } else {
      const openEntry = { id: `h-open-${Date.now()}`, action: 'Task opened', user: commentAuthorName || 'You', time: new Date().toISOString() };
      allTaskHistoryRef.current[taskId] = [openEntry];
      setTaskHistory([openEntry]);
    }
    const column = columns.find(col => col.id === columnId);
    const task = column?.tasks.find(t => t.id === taskId);

    if (task) {
      setEditingTask({ taskId, columnId });
      setTaskComments(task.comments || []);
      setCommentInput('');
      setShowMentionDropdown(false);

      startTransition(() => {
        setNewTaskData({
          title: task.title,
          description: task.description,
          url: Array.isArray(task.url) ? task.url : (task.url ? [task.url] : []),
          drive: Array.isArray(task.drive) ? task.drive : (task.drive ? [task.drive] : []),
          figma: Array.isArray(task.figma) ? task.figma : (task.figma ? [task.figma] : []),
          jira: Array.isArray(task.jira) ? task.jira : (task.jira ? [task.jira] : []),
          docs: Array.isArray(task.docs) ? task.docs : (task.docs ? [task.docs] : []),
          image: task.image || '',
          tags: task.tags || [],
          priority: task.priority || 'medium',
          notes: task.notes || '',
          subtasks: task.subtasks || [],
          reminder: task.reminder || '',
          project: task.project || 'Engineering Board',
          assignee: task.assignee || 'Unassigned',
          startDate: task.startDate || '',
          sprint: task.sprint || '',
          storyPoints: task.storyPoints || '',
          reporter: task.reporter || 'Akash Sharma',
          status: task.status || columnId,
          dueDate: task.dueDate || ''
        });

        setShowUrlField(Array.isArray(task.url) ? task.url.length > 0 : !!task.url);
        setShowDriveField(Array.isArray(task.drive) ? task.drive.length > 0 : !!task.drive);
        setShowFigmaField(Array.isArray(task.figma) ? task.figma.length > 0 : !!task.figma);
        setShowJiraField(Array.isArray(task.jira) ? task.jira.length > 0 : !!task.jira);
        setShowDocsField(Array.isArray(task.docs) ? task.docs.length > 0 : !!task.docs);
        setShowImageField(!!task.image);
        setShowTagsField(!!(task.tags && task.tags.length > 0));
        setShowNotesField(!!task.notes);
        setShowSubtasksField(!!(task.subtasks && task.subtasks.length > 0) || task.subtasks !== undefined);
        setShowReminderField(!!task.reminder);
      });

      // Snapshot the task state so handleUpdateTask can diff against it
      originalTaskRef.current = {
        title: task.title, description: task.description, status: task.status || columnId,
        priority: task.priority || 'medium', assignee: task.assignee || 'Unassigned',
        tags: task.tags || [], dueDate: task.dueDate || '', notes: task.notes || '',
        url: Array.isArray(task.url) ? task.url : (task.url ? [task.url] : []),
        drive: Array.isArray(task.drive) ? task.drive : (task.drive ? [task.drive] : []),
        figma: Array.isArray(task.figma) ? task.figma : (task.figma ? [task.figma] : []),
        jira: Array.isArray(task.jira) ? task.jira : (task.jira ? [task.jira] : []),
        docs: Array.isArray(task.docs) ? task.docs : (task.docs ? [task.docs] : []),
        image: task.image || '', subtasks: task.subtasks || [], reminder: task.reminder || '',
        project: task.project || 'Engineering Board', startDate: task.startDate || '',
        sprint: task.sprint || '', storyPoints: task.storyPoints || '',
        reporter: task.reporter || 'Akash Sharma',
      };
    }
  };

  const handleCommentInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setCommentInput(val);
    const cursor = e.target.selectionStart || 0;
    const textBefore = val.slice(0, cursor);
    const atIdx = textBefore.lastIndexOf('@');
    if (atIdx !== -1) {
      const query = textBefore.slice(atIdx + 1);
      if (!query.includes(' ')) {
        setMentionQuery(query);
        setShowMentionDropdown(true);
        return;
      }
    }
    setShowMentionDropdown(false);
  };

  const insertMention = (name: string) => {
    const el = commentTextareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart || 0;
    const textBefore = commentInput.slice(0, cursor);
    const atIdx = textBefore.lastIndexOf('@');
    const before = commentInput.slice(0, atIdx);
    const after = commentInput.slice(cursor);
    const newVal = `${before}@${name} ${after}`;
    setCommentInput(newVal);
    setShowMentionDropdown(false);
    setTimeout(() => {
      el.focus();
      const pos = atIdx + name.length + 2;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleAddComment = () => {
    if (!commentInput.trim() || !editingTask) return;
    const newComment: Comment = {
      id: `c-${Date.now()}`,
      text: commentInput.trim(),
      authorId: commentAuthorId,
      authorName: commentAuthorName,
      createdAt: new Date().toISOString()
    };
    const updated = [...taskComments, newComment];
    setTaskComments(updated);
    setCommentInput('');
    setShowMentionDropdown(false);
    addHistoryEntry(`Added a comment`);
    const nextColumns = columns.map(col => ({
      ...col,
      tasks: col.tasks.map(t =>
        t.id === editingTask.taskId && col.id === editingTask.columnId
          ? { ...t, comments: updated }
          : t
      )
    }));
    setColumns(nextColumns);
    const board = taskboardRef.current;
    if (board) {
      enqueueSave(nextColumns, taskboardNameRef.current);
    }
  };

  const handleRemoveSubtask = (subtaskId: string) => {
    setNewTaskData({ ...newTaskData, subtasks: newTaskData.subtasks.filter(st => st.id !== subtaskId) });
  };

  const handleToggleSubtaskInCard = (columnId: string, taskId: string, subtaskId: string) => {
    const nextColumns = columns.map(col => {
      if (col.id === columnId) {
        return {
          ...col,
          tasks: col.tasks.map(task => {
            if (task.id === taskId && task.subtasks) {
              const updatedSubtasks = task.subtasks.map(st =>
                st.id === subtaskId ? { ...st, completed: !st.completed } : st
              );
              const sortedSubtasks = updatedSubtasks.sort((a, b) => {
                if (a.completed === b.completed) return 0;
                return a.completed ? 1 : -1;
              });
              return { ...task, subtasks: sortedSubtasks };
            }
            return task;
          })
        };
      }
      return col;
    });
    setColumns(nextColumns);
    const board = taskboardRef.current;
    if (board) {
      enqueueSave(nextColumns, taskboardNameRef.current);
    }
  };

  const handleUpdateTask = (columnId: string) => {
    if (!editingTask) return;

    // Title is the only required field — description optional so edits always save.
    if (!newTaskData.title.trim()) {
      alert('Title is required');
      return;
    }

    const targetColumnId = newTaskData.status || columnId;

    // Compute updated columns synchronously so we can save immediately!
    let nextColumns = [...columns];
    if (targetColumnId !== columnId) {
      let foundTask: Task | null = null;
      const columnsWithRemoved = columns.map(col => {
        if (col.id === columnId) {
          foundTask = col.tasks.find(t => t.id === editingTask.taskId) || null;
          return { ...col, tasks: col.tasks.filter(t => t.id !== editingTask.taskId) };
        }
        return col;
      });

      if (foundTask) {
        const updatedTask: Task = {
          ...(foundTask as Task),
          title: newTaskData.title,
          description: newTaskData.description,
          tags: newTaskData.tags,
          priority: newTaskData.priority,
          image: newTaskData.image,
          url: newTaskData.url,
          drive: newTaskData.drive,
          figma: newTaskData.figma,
          jira: newTaskData.jira,
          docs: newTaskData.docs,
          notes: newTaskData.notes,
          subtasks: newTaskData.subtasks,
          reminder: newTaskData.reminder || undefined,
          project: newTaskData.project,
          assignee: newTaskData.assignee,
          startDate: newTaskData.startDate,
          sprint: newTaskData.sprint,
          storyPoints: newTaskData.storyPoints,
          reporter: newTaskData.reporter,
          status: targetColumnId,
          dueDate: newTaskData.dueDate || undefined
        };

        nextColumns = columnsWithRemoved.map(col => {
          if (col.id === targetColumnId) {
            return { ...col, tasks: [...col.tasks, updatedTask] };
          }
          return col;
        });
      }
    } else {
      nextColumns = columns.map(col => {
        if (col.id === columnId) {
          return {
            ...col,
            tasks: col.tasks.map(task => {
              if (task.id === editingTask.taskId) {
                return {
                  ...task,
                  title: newTaskData.title,
                  description: newTaskData.description,
                  tags: newTaskData.tags,
                  priority: newTaskData.priority,
                  image: newTaskData.image,
                  url: newTaskData.url,
                  drive: newTaskData.drive,
                  figma: newTaskData.figma,
                  jira: newTaskData.jira,
                  docs: newTaskData.docs,
                  notes: newTaskData.notes,
                  subtasks: newTaskData.subtasks,
                  reminder: newTaskData.reminder || undefined,
                  project: newTaskData.project,
                  assignee: newTaskData.assignee,
                  startDate: newTaskData.startDate,
                  sprint: newTaskData.sprint,
                  storyPoints: newTaskData.storyPoints,
                  reporter: newTaskData.reporter,
                  status: targetColumnId,
                  dueDate: newTaskData.dueDate || undefined
                };
              }
              return task;
            })
          };
        }
        return col;
      });
    }

    // Build detailed history entries by diffing original vs new
    if (originalTaskRef.current) {
      const changes = buildHistoryEntries(originalTaskRef.current as typeof newTaskData, newTaskData);
      if (changes.length === 0) {
        addHistoryEntry('Task saved (no changes)');
      } else {
        changes.forEach(change => addHistoryEntry(change));
      }
    } else {
      addHistoryEntry(`Task updated: "${newTaskData.title}"`);
    }
    // Update snapshot for subsequent saves in the same session
    originalTaskRef.current = { ...newTaskData };

    startTransition(() => {
      setColumns(nextColumns);
      const board = taskboardRef.current;
      if (board) {
        enqueueSave(nextColumns, taskboardNameRef.current);
      }
    });

    setNewTaskData({
      title: '',
      description: '',
      url: [],
      drive: [],
      figma: [],
      jira: [],
      docs: [],
      image: '',
      tags: [],
      priority: 'medium' as 'lowest' | 'low' | 'medium' | 'high' | 'highest',
      notes: '',
      subtasks: [],
      reminder: '',
      project: 'Engineering Board',
      assignee: 'Unassigned',
      startDate: '',
      sprint: '',
      storyPoints: '',
      reporter: 'Akash Sharma',
      status: '',
      dueDate: ''
    });
    setCurrentTag('');
    setCurrentUrl('');
    setCurrentDrive('');
    setCurrentFigma('');
    setCurrentJira('');
    setCurrentDocs('');
    setCurrentSubtask('');
    setShowAddForm(null);
    setEditingTask(null);
    setShowUrlField(false);
    setShowDriveField(false);
    setShowFigmaField(false);
    setShowJiraField(false);
    setShowDocsField(false);
    setShowImageField(false);
    setShowTagsField(false);
    setShowNotesField(false);
    setShowSubtasksField(false);
    setShowReminderField(false);
  };

  const handleRemoveUrl   = (url: string)  => setNewTaskData(prev => ({ ...prev, url:   prev.url.filter(u => u !== url) }));
  const handleRemoveDrive = (link: string) => setNewTaskData(prev => ({ ...prev, drive: prev.drive.filter(l => l !== link) }));
  const handleRemoveFigma = (link: string) => setNewTaskData(prev => ({ ...prev, figma: prev.figma.filter(l => l !== link) }));
  const handleRemoveJira  = (link: string) => setNewTaskData(prev => ({ ...prev, jira:  prev.jira.filter(l => l !== link) }));
  const handleRemoveDocs  = (doc: string)  => setNewTaskData(prev => ({ ...prev, docs:  prev.docs.filter(d => d !== doc) }));
  const handleRemoveTag   = (tag: string)  => setNewTaskData(prev => ({ ...prev, tags:  prev.tags.filter(t => t !== tag) }));

  const handleAddUrl = () => {
    if (!currentUrl.trim() || newTaskData.url.length >= 3) return;
    setNewTaskData(prev => ({ ...prev, url: [...prev.url, currentUrl.trim()] }));
    setCurrentUrl('');
  };
  const handleAddDrive = () => {
    if (!currentDrive.trim() || newTaskData.drive.length >= 3) return;
    setNewTaskData(prev => ({ ...prev, drive: [...prev.drive, currentDrive.trim()] }));
    setCurrentDrive('');
  };
  const handleAddFigma = () => {
    if (!currentFigma.trim() || newTaskData.figma.length >= 3) return;
    setNewTaskData(prev => ({ ...prev, figma: [...prev.figma, currentFigma.trim()] }));
    setCurrentFigma('');
  };
  const handleAddJira = () => {
    if (!currentJira.trim() || newTaskData.jira.length >= 3) return;
    setNewTaskData(prev => ({ ...prev, jira: [...prev.jira, currentJira.trim()] }));
    setCurrentJira('');
  };
  const handleAddDocs = () => {
    if (!currentDocs.trim() || newTaskData.docs.length >= 3) return;
    setNewTaskData(prev => ({ ...prev, docs: [...prev.docs, currentDocs.trim()] }));
    setCurrentDocs('');
  };
  const handleAddTag = () => {
    if (!currentTag.trim() || newTaskData.tags.includes(currentTag.trim())) return;
    setNewTaskData(prev => ({ ...prev, tags: [...prev.tags, currentTag.trim()] }));
    setCurrentTag('');
  };

  const handleAddSubtask = () => {
    if (!currentSubtask.trim()) return;
    const newSubtask: SubTask = { id: `st-${Date.now()}`, text: currentSubtask.trim(), completed: false };
    setNewTaskData(prev => ({ ...prev, subtasks: [...prev.subtasks, newSubtask] }));
    setCurrentSubtask('');
  };

  const handleToggleSubtask = (subtaskId: string) => {
    setNewTaskData(prev => ({
      ...prev,
      subtasks: prev.subtasks.map(st =>
        st.id === subtaskId ? { ...st, completed: !st.completed } : st
      )
    }));
  };

  const handleDeleteTask = (columnId: string, taskId: string) => {
    const nextColumns = columns.map(col => {
      if (col.id === columnId) return { ...col, tasks: col.tasks.filter(task => task.id !== taskId) };
      return col;
    });
    setColumns(nextColumns);
    const board = taskboardRef.current;
    if (board) {
      enqueueSave(nextColumns, taskboardNameRef.current);
    }
  };

  const handleMoveTask = (taskId: string, sourceColumnId: string, targetColumnId: string) => {
    if (sourceColumnId === targetColumnId) return;
    let movedTask: Task | null = null;
    const nextColumns = columns.map(col => {
      if (col.id === sourceColumnId) {
        movedTask = col.tasks.find(t => t.id === taskId) || null;
        return { ...col, tasks: col.tasks.filter(t => t.id !== taskId) };
      }
      return col;
    }).map(col => {
      if (col.id === targetColumnId && movedTask) {
        return { ...col, tasks: [...col.tasks, { ...movedTask, status: targetColumnId }] };
      }
      return col;
    });
    setColumns(nextColumns);
    const board = taskboardRef.current;
    if (board) {
      enqueueSave(nextColumns, taskboardNameRef.current);
    }
  };

  // Returns true if the current user is allowed to move/edit this task.
  // Admins/owners can move any task; members can only move their own assigned tasks.
  const canMoveTask = (task: Task): boolean => {
    if (isAdmin) return true;
    const currentName = commentAuthorName;
    return task.assignee === currentName || task.assignee === 'Unassigned' || !task.assignee;
  };

  // Returns true if the target column looks like a "completed" column.
  const isCompletedColumn = (colId: string): boolean => {
    const col = columns.find(c => c.id === colId);
    if (!col) return false;
    const t = col.title.toLowerCase();
    return t.includes('complet') || t.includes('done') || t.includes('closed') || t.includes('finish');
  };

  // Execute a confirmed or unrestricted drop
  const executeDrop = (task: Task, sourceColumnId: string, targetColumnId: string, dropIndex?: number) => {
    let nextColumns: Column[];
    if (sourceColumnId === targetColumnId) {
      const column = columns.find(col => col.id === sourceColumnId);
      if (!column) return;
      const currentIndex = column.tasks.findIndex(t => t.id === task.id);
      let targetIndex = dropIndex !== undefined ? dropIndex : column.tasks.length;
      if (dropIndex !== undefined && currentIndex < dropIndex) targetIndex = dropIndex - 1;
      nextColumns = columns.map(col => {
        if (col.id === sourceColumnId) {
          const newTasks = [...col.tasks];
          newTasks.splice(currentIndex, 1);
          newTasks.splice(targetIndex, 0, task);
          return { ...col, tasks: newTasks };
        }
        return col;
      });
    } else {
      nextColumns = columns.map(col => {
        if (col.id === sourceColumnId) return { ...col, tasks: col.tasks.filter(t => t.id !== task.id) };
        if (col.id === targetColumnId) {
          const newTasks = [...col.tasks];
          if (dropIndex !== undefined) { newTasks.splice(dropIndex, 0, task); }
          else { newTasks.push(task); }
          return { ...col, tasks: newTasks };
        }
        return col;
      });
    }
    setColumns(nextColumns);
    setDraggedTask(null); setDragOverColumnId(null); setDragOverTaskId(null);
    const board = taskboardRef.current;
    if (board) enqueueSave(nextColumns, taskboardNameRef.current);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, task: Task, columnId: string) => {
    if (!canMoveTask(task)) {
      e.preventDefault();
      return;
    }
    setDraggedTask({ task, sourceColumnId: columnId });
    e.dataTransfer.effectAllowed = 'move';
    // Store reference to the element before setTimeout
    const element = e.currentTarget;
    setTimeout(() => {
      if (element) {
        element.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedTask(null);
    setDragOverColumnId(null);
    setDragOverTaskId(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumnId(columnId);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Only clear if we're leaving the column entirely, not just moving between children
    if (e.currentTarget === e.target) {
      setDragOverColumnId(null);
    }
  };

  const handleTaskDragOver = (e: React.DragEvent<HTMLDivElement>, taskId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTaskId(taskId);
  };

  const handleTaskDrop = (e: React.DragEvent<HTMLDivElement>, targetTaskId: string, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedTask || draggedTask.task.id === targetTaskId) return;

    const column = columns.find(col => col.id === columnId);
    if (!column) return;

    const dropIndex = column.tasks.findIndex(t => t.id === targetTaskId);
    handleDrop(e, columnId, dropIndex);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetColumnId: string, dropIndex?: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedTask) return;
    const { task, sourceColumnId } = draggedTask;

    // Permission check — only owner/admin or task assignee can move
    if (!canMoveTask(task)) {
      setDraggedTask(null); setDragOverColumnId(null); setDragOverTaskId(null);
      return;
    }

    // Confirmation for cross-column move into a "completed" column
    if (sourceColumnId !== targetColumnId && isCompletedColumn(targetColumnId)) {
      setPendingDrop({ task, sourceColumnId, targetColumnId, dropIndex });
      setDraggedTask(null); setDragOverColumnId(null); setDragOverTaskId(null);
      return;
    }

    executeDrop(task, sourceColumnId, targetColumnId, dropIndex);
  };

  // Get all unique tags from all tasks
  // ── Board scroll navigation ───────────────────────────────
  const updateScrollButtons = () => {
    const el = boardScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scrollBoard = (dir: 'left' | 'right') => {
    const el = boardScrollRef.current;
    if (!el) return;
    const colWidth = 300; // approximate column width + gap
    el.scrollBy({ left: dir === 'right' ? colWidth : -colWidth, behavior: 'smooth' });
  };

  const jumpToColumn = (colId: string) => {
    const el = boardScrollRef.current;
    if (!el) return;
    const colEl = el.querySelector(`[data-col-id="${colId}"]`) as HTMLElement;
    if (colEl) colEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    setShowJumpMenu(false);
  };
  // ─────────────────────────────────────────────────────────

  const getAllTags = () => {
    const allTags = new Set<string>();
    columns.forEach(column => {
      column.tasks.forEach(task => {
        if (task.tags) {
          task.tags.forEach(tag => allTags.add(tag));
        }
      });
    });
    return Array.from(allTags);
  };

  const handleToggleFilterTag = (tag: string) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleToggleFilterPriority = (priority: string) => {
    setFilters(prev => ({
      ...prev,
      priority: prev.priority.includes(priority)
        ? prev.priority.filter(p => p !== priority)
        : [...prev.priority, priority]
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      tags: [],
      priority: [],
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleClearSort = () => {
    setSortConfig({
      field: null,
      direction: null
    });
  };

  const handleSortClick = (field: 'title' | 'priority' | 'created') => {
    setSortConfig(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };

  const getFilteredAndSortedTasks = (tasks: Task[]) => {
    let result = [...tasks];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.title.toLowerCase().includes(q) ||
        getDescriptionText(t.description).toLowerCase().includes(q) ||
        t.tags.some(tag => tag.toLowerCase().includes(q))
      );
    }
    if (filters.tags.length > 0) {
      result = result.filter(t => t.tags && filters.tags.some(tag => t.tags.includes(tag)));
    }
    if (filters.priority.length > 0) {
      result = result.filter(t => t.priority && filters.priority.includes(t.priority));
    }
    if (filters.dateFrom) {
      result = result.filter(t => t.createdAt && new Date(t.createdAt) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      result = result.filter(t => t.createdAt && new Date(t.createdAt) <= new Date(filters.dateTo));
    }
    if (sortConfig.field && sortConfig.direction) {
      const priorityOrder: Record<string, number> = { lowest: 1, low: 2, medium: 3, high: 4, highest: 5 };
      result.sort((a, b) => {
        let cmp = 0;
        if (sortConfig.field === 'title') cmp = a.title.localeCompare(b.title);
        else if (sortConfig.field === 'priority') cmp = (priorityOrder[a.priority || 'medium'] || 3) - (priorityOrder[b.priority || 'medium'] || 3);
        else if (sortConfig.field === 'created') cmp = (a.createdAt || '').localeCompare(b.createdAt || '');
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  };

  return (
    <div className="taskboard-container">
      {/* Header - Combined Single Row */}
      <div className="taskboard-header-row-1">
        <div className="taskboard-title-section">
          <button className="back-button btn-primary-cta" onClick={onBackToList}>
            Back
          </button>
          <input
            type="text"
            className="taskboard-name-input"
            value={taskboardName}
            onChange={(e) => setTaskboardName(e.target.value)}
            placeholder="Task board name"
          />
          {isSaving && <span className="saving-indicator">Saving...</span>}
        </div>

        <div className="search-and-actions-container">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'board' ? 'active' : ''}`}
                onClick={() => setViewMode('board')}
              >
                <CardViewIcon width="16" height="16" />
                Board View
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <ListViewIcon width="16" height="16" />
                List View
              </button>
              <button
                className={`view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                onClick={() => setViewMode('calendar')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.8"
                     strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                Calendar View
              </button>
            </div>

            <div className="search-container">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search Tasks"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Filter Dropdown */}
            <div className="filter-sort-container" ref={filterDropdownRef}>
              <button
                className={`filter-btn ${filters.tags.length > 0 || filters.priority.length > 0 || filters.dateFrom || filters.dateTo ? 'active' : ''}`}
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                </svg>
                Filter
                {(filters.tags.length > 0 || filters.priority.length > 0 || filters.dateFrom || filters.dateTo) && (
                  <span className="filter-badge">{filters.tags.length + filters.priority.length + (filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0)}</span>
                )}
              </button>

              {showFilterDropdown && (
                <div className="filter-dropdown">
                  <div className="filter-dropdown-header">
                    <h3>Filter Tasks</h3>
                    <button className="clear-filters-btn" onClick={handleClearFilters}>Clear All</button>
                  </div>

                  <div className="filter-section">
                    <label className="filter-section-label">Tags</label>
                    <div className="filter-tags-searchable" ref={tagsDropdownRef}>
                      {/* Search input with selected tags */}
                      <div
                        className="filter-tags-input-wrapper"
                        onClick={() => setShowTagsDropdown(true)}
                      >
                        <div className="filter-tags-selected-inline">
                          {filters.tags.map(tag => (
                            <span key={tag} className="filter-tag-chip-small">
                              {tag}
                              <button
                                className="filter-tag-remove-small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleFilterTag(tag);
                                }}
                                aria-label={`Remove ${tag}`}
                              >
                                Ã—
                              </button>
                            </span>
                          ))}
                          <input
                            type="text"
                            className="filter-tags-search-input"
                            placeholder={filters.tags.length === 0 ? "Search tags or type *..." : ""}
                            value={tagSearchQuery}
                            onChange={(e) => {
                              setTagSearchQuery(e.target.value);
                              setShowTagsDropdown(true);
                            }}
                            onFocus={() => setShowTagsDropdown(true)}
                          />
                        </div>
                      </div>

                      {/* Dropdown list */}
                      {showTagsDropdown && (
                        <div className="filter-tags-dropdown">
                          {(() => {
                            const allTags = getAllTags();
                            const availableTags = allTags.filter(tag => !filters.tags.includes(tag));
                            const showAll = tagSearchQuery === '*' || tagSearchQuery === '';
                            const filteredTags = showAll
                              ? availableTags
                              : availableTags.filter(tag =>
                                  tag.toLowerCase().includes(tagSearchQuery.toLowerCase())
                                );

                            return filteredTags.length > 0 ? (
                              filteredTags.map(tag => (
                                <button
                                  key={tag}
                                  className="filter-tag-dropdown-option"
                                  onClick={() => {
                                    handleToggleFilterTag(tag);
                                    setTagSearchQuery('');
                                  }}
                                >
                                  {tag}
                                </button>
                              ))
                            ) : (
                              <p className="no-options">
                                {allTags.length === 0 ? 'No tags available' :
                                 availableTags.length === 0 ? 'All tags selected' :
                                 'No matching tags'}
                              </p>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="filter-section">
                    <label className="filter-section-label">Priority</label>
                    <div className="filter-priority-icons">
                      <button
                        className={`filter-priority-btn ${filters.priority.includes('lowest') ? 'active' : ''}`}
                        onClick={() => handleToggleFilterPriority('lowest')}
                        title="Lowest"
                      >
                        <PriorityLowestIcon width="20" height="20" />
                      </button>
                      <button
                        className={`filter-priority-btn ${filters.priority.includes('low') ? 'active' : ''}`}
                        onClick={() => handleToggleFilterPriority('low')}
                        title="Low"
                      >
                        <PriorityLowIcon width="20" height="20" />
                      </button>
                      <button
                        className={`filter-priority-btn ${filters.priority.includes('medium') ? 'active' : ''}`}
                        onClick={() => handleToggleFilterPriority('medium')}
                        title="Medium"
                      >
                        <PriorityMediumIcon width="20" height="20" />
                      </button>
                      <button
                        className={`filter-priority-btn ${filters.priority.includes('high') ? 'active' : ''}`}
                        onClick={() => handleToggleFilterPriority('high')}
                        title="High"
                      >
                        <PriorityHighIcon width="20" height="20" />
                      </button>
                      <button
                        className={`filter-priority-btn ${filters.priority.includes('highest') ? 'active' : ''}`}
                        onClick={() => handleToggleFilterPriority('highest')}
                        title="Highest"
                      >
                        <PriorityHighestIcon width="20" height="20" />
                      </button>
                    </div>
                  </div>

                  <div className="filter-section">
                    <label className="filter-section-label">Created Date</label>
                    <div className="date-range-inputs">
                      <div className="date-input-group">
                        <label>From</label>
                        <input
                          type="date"
                          value={filters.dateFrom}
                          onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                        />
                      </div>
                      <div className="date-input-group">
                        <label>To</label>
                        <input
                          type="date"
                          value={filters.dateTo}
                          onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="filter-sort-container" ref={sortDropdownRef}>
              <button
                className={`sort-btn ${sortConfig.field !== null ? 'active' : ''}`}
                onClick={() => setShowSortDropdown(!showSortDropdown)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 5h10M11 9h7M11 13h4M3 17l3 3 3-3M6 3v17"></path>
                </svg>
                Sort
              </button>

              {showSortDropdown && (
                <div className="sort-dropdown">
                  <div className="sort-dropdown-header">
                    <h3>Sort Tasks</h3>
                    <button className="clear-filters-btn" onClick={handleClearSort}>Clear All</button>
                  </div>

                  <div className="sort-options">
                    <button
                      className={`sort-option ${sortConfig.field === 'title' ? 'active' : ''}`}
                      onClick={() => handleSortClick('title')}
                    >
                      <span>Title</span>
                      {sortConfig.field === 'title' && sortConfig.direction && (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{ transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'none' }}
                        >
                          <path d="M12 19V5M5 12l7-7 7 7"></path>
                        </svg>
                      )}
                    </button>

                    <button
                      className={`sort-option ${sortConfig.field === 'priority' ? 'active' : ''}`}
                      onClick={() => handleSortClick('priority')}
                    >
                      <span>Priority</span>
                      {sortConfig.field === 'priority' && sortConfig.direction && (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{ transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'none' }}
                        >
                          <path d="M12 19V5M5 12l7-7 7 7"></path>
                        </svg>
                      )}
                    </button>

                    <button
                      className={`sort-option ${sortConfig.field === 'created' ? 'active' : ''}`}
                      onClick={() => handleSortClick('created')}
                    >
                      <span>Created Date</span>
                      {sortConfig.field === 'created' && sortConfig.direction && (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{ transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'none' }}
                        >
                          <path d="M12 19V5M5 12l7-7 7 7"></path>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              className="add-idea-btn btn-primary-cta"
              onClick={() => setShowAddForm(columns[0]?.id || '')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Idea
            </button>
          </div>
      </div>

      {/* Board View */}
      {viewMode === 'board' && (
        <div className="board-nav-wrapper">
          {/* Left scroll arrow */}
          {canScrollLeft && (
            <button className="board-scroll-btn board-scroll-left" onClick={() => scrollBoard('left')} title="Scroll left">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
          )}

          {/* Jump to Column dropdown */}
          {columns.length > 3 && (
            <div className="board-jump-wrap">
              <button className="board-jump-btn" onClick={() => setShowJumpMenu(v => !v)} title="Jump to column">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                Jump to
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {showJumpMenu && (
                <div className="board-jump-menu">
                  {columns.map(col => (
                    <button key={col.id} className="board-jump-item" onClick={() => jumpToColumn(col.id)}>
                      <span className="board-jump-dot" style={{ background: col.color }} />
                      {col.title}
                      <span className="board-jump-count">{col.tasks.length}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div
            className="board-columns"
            ref={boardScrollRef}
            onScroll={updateScrollButtons}
          >
            {columns.map((column) => (
              <div
                key={column.id}
                data-col-id={column.id}
                className={`board-column ${dragOverColumnId === column.id ? 'drag-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                <div className="column-header">
                  <div className="column-title">
                    <span>{column.title}</span>
                    <span className="task-count">{column.tasks.length}</span>
                  </div>
                  <button
                    className="add-task-btn"
                    onClick={() => setShowAddForm(column.id)}
                  >
                    +
                  </button>
                </div>

                <div className="column-tasks">
                  {getFilteredAndSortedTasks(column.tasks).map((task) => (
                    <React.Fragment key={task.id}>
                      <div
                        className={`task-card ${dragOverTaskId === task.id ? 'drag-over-task' : ''}`}
                        draggable={canMoveTask(task)}
                        onDragStart={(e) => handleDragStart(e, task, column.id)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleTaskDragOver(e, task.id)}
                        onDrop={(e) => handleTaskDrop(e, task.id, column.id)}
                      >
                      {/* Tags row — with assignee avatar on the right */}
                      {(task.tags && task.tags.length > 0) || (task.assignee && task.assignee !== 'Unassigned') ? (
                        <div className="task-card-header">
                          <div className="task-tags">
                            {(task.tags || []).map((tag, index) => (
                              <span key={index} className={`task-tag ${tag.toLowerCase()}`}>{tag}</span>
                            ))}
                          </div>
                          {task.assignee && task.assignee !== 'Unassigned' && (
                            <div className="task-assignee-wrap">
                              <div
                                className="task-assignee-avatar"
                                style={{ background: `hsl(${(task.assignee.charCodeAt(0) * 37) % 360}, 60%, 45%)` }}
                              >
                                {task.assignee.charAt(0).toUpperCase()}
                              </div>
                              <span className="task-assignee-tooltip">{task.assignee}</span>
                            </div>
                          )}
                        </div>
                      ) : null}

                      {/* Image (if present) */}
                      {task.image && (
                        <div
                          className="task-image"
                          onClick={(e) => {
                            e.stopPropagation();
                            setZoomedImage(task.image || null);
                          }}
                          style={{ cursor: 'pointer' }}
                          title="Click to zoom"
                        >
                          <img src={task.image} alt={task.title} />
                        </div>
                      )}

                      {/* Lock icon for tasks the current user cannot move */}
                      {!canMoveTask(task) && (
                        <div className="task-readonly-badge" title="You can only move tasks assigned to you">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                          Read-only
                        </div>
                      )}

                      {/* Title with Priority icon inline */}
                      <div className="task-title-row">
                        <h3 className="task-title">{task.title}</h3>
                        {task.priority && (
                          <div className="task-priority-icon">
                            {task.priority === 'lowest' && <PriorityLowestIcon width="20" height="20" />}
                            {task.priority === 'low' && <PriorityLowIcon width="20" height="20" />}
                            {task.priority === 'medium' && <PriorityMediumIcon width="20" height="20" />}
                            {task.priority === 'high' && <PriorityHighIcon width="20" height="20" />}
                            {task.priority === 'highest' && <PriorityHighestIcon width="20" height="20" />}
                          </div>
                        )}
                      </div>

                      {/* Description preview on card — plain text, max 80 chars */}
                      {getDescriptionText(task.description) && (
                        <p className="task-description">
                          {getDescriptionText(task.description).slice(0, 80)}{getDescriptionText(task.description).length > 80 ? '…' : ''}
                        </p>
                      )}

                      {/* Links Section */}
                      {((task.figma && (Array.isArray(task.figma) ? task.figma.length > 0 : task.figma)) || (task.drive && (Array.isArray(task.drive) ? task.drive.length > 0 : task.drive)) || (task.jira && (Array.isArray(task.jira) ? task.jira.length > 0 : task.jira)) || (task.docs && (Array.isArray(task.docs) ? task.docs.length > 0 : task.docs)) || (task.url && (Array.isArray(task.url) ? task.url.length > 0 : task.url))) && (
                        <div className="task-links-container">
                          {task.figma && (Array.isArray(task.figma) ? task.figma.length > 0 : task.figma) && (
                            <div className="task-links-row">
                              {(Array.isArray(task.figma) ? task.figma : [task.figma]).map((figmaLink, index) => (
                                <a
                                  key={`figma-${index}`}
                                  href={figmaLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="task-link-item task-link-clickable"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <FigmaIcon width="14" height="14" />
                                  <span>{figmaLink.includes('figma.com') ? 'Figma design' : figmaLink.length > 30 ? figmaLink.substring(0, 30) + '...' : figmaLink}</span>
                                </a>
                              ))}
                            </div>
                          )}
                          {task.drive && (Array.isArray(task.drive) ? task.drive.length > 0 : task.drive) && (
                            <div className="task-links-row">
                              {(Array.isArray(task.drive) ? task.drive : [task.drive]).map((driveLink, index) => (
                                <a
                                  key={`drive-${index}`}
                                  href={driveLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="task-link-item task-link-clickable"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <GoogleDriveIcon width="14" height="14" />
                                  <span>{driveLink.includes('drive.google.com') ? 'Google Drive' : driveLink.length > 30 ? driveLink.substring(0, 30) + '...' : driveLink}</span>
                                </a>
                              ))}
                            </div>
                          )}
                          {task.jira && (Array.isArray(task.jira) ? task.jira.length > 0 : task.jira) && (
                            <div className="task-links-row">
                              {(Array.isArray(task.jira) ? task.jira : [task.jira]).map((jiraLink, index) => (
                                <a
                                  key={`jira-${index}`}
                                  href={jiraLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="task-link-item task-link-clickable"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <JiraIcon width="14" height="14" />
                                  <span>{jiraLink.match(/[A-Z]+-\d+/) ? jiraLink.match(/[A-Z]+-\d+/)?.[0] : 'Jira ticket'}</span>
                                </a>
                              ))}
                            </div>
                          )}
                          {task.docs && (Array.isArray(task.docs) ? task.docs.length > 0 : task.docs) && (
                            <div className="task-links-row">
                              {(Array.isArray(task.docs) ? task.docs : [task.docs]).map((docsLink, index) => (
                                <a
                                  key={`docs-${index}`}
                                  href={docsLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="task-link-item task-link-clickable"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <GoogleDocsIcon width="14" height="14" />
                                  <span>Google Docs</span>
                                </a>
                              ))}
                            </div>
                          )}
                          {task.url && (Array.isArray(task.url) ? task.url.length > 0 : task.url) && (
                            <div className="task-links-row">
                              {(Array.isArray(task.url) ? task.url : [task.url]).map((urlLink, index) => (
                                <a
                                  key={`url-${index}`}
                                  href={urlLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="task-link-item task-link-clickable"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <URLIcon width="14" height="14" />
                                  <span>{urlLink.length > 30 ? urlLink.substring(0, 30) + '...' : urlLink}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Notes Section */}
                      {task.notes && (
                        <div className="task-notes">
                          <div className="task-notes-label">Notes:</div>
                          <div className="task-notes-content">{task.notes}</div>
                        </div>
                      )}

                      {/* Subtasks Section */}
                      {task.subtasks && task.subtasks.length > 0 && (
                        <div className="task-subtasks">
                          {task.subtasks.map((subtask) => (
                            <div key={subtask.id} className="task-subtask-item">
                              <input
                                type="checkbox"
                                checked={subtask.completed}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleToggleSubtaskInCard(column.id, task.id, subtask.id);
                                }}
                                className="task-subtask-checkbox"
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  minWidth: '12px',
                                  maxWidth: '12px',
                                  minHeight: '12px',
                                  maxHeight: '12px',
                                  margin: '0',
                                  padding: '0'
                                }}
                              />
                              <span className={`task-subtask-text ${subtask.completed ? 'completed' : ''}`}>
                                {subtask.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Progress Bar - Show always (gray line if no subtasks) */}
                      <div className="task-progress-bar">
                        {task.subtasks && task.subtasks.length > 0 ? (
                          <div
                            className="task-progress-fill"
                            style={{
                              width: `${(task.subtasks.filter(st => st.completed).length / task.subtasks.length) * 100}%`
                            }}
                          ></div>
                        ) : (
                          <div className="task-progress-placeholder"></div>
                        )}
                      </div>

                      {/* Footer with Date, Assignee and Action Buttons */}
                      <div className="task-card-footer">
                        {task.createdAt && (
                          <div className="task-date">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                            <span>{task.createdAt}</span>
                          </div>
                        )}
                        <div className="task-action-buttons">
                          {task.reminder && new Date(task.reminder).getTime() > new Date().getTime() && (
                            <div
                              className="task-reminder-indicator"
                              title={`Reminder: ${new Date(task.reminder).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}`}
                            >
                              <ReminderIcon width="14" height="14" />
                            </div>
                          )}
                          <button
                            className="task-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTask(column.id, task.id);
                            }}
                            title="Edit task"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button
                            className="task-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTask(column.id, task.id);
                            }}
                            title="Delete task"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    </React.Fragment>
                  ))}

                  <button
                    className="add-card-btn"
                    onClick={() => setShowAddForm(column.id)}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Right scroll arrow */}
          {canScrollRight && (
            <button className="board-scroll-btn board-scroll-right" onClick={() => scrollBoard('right')} title="Scroll right">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          )}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
            <div className="list-view">
            {(() => {
              // Get all tasks from all columns with their column info
              const allTasks = columns.flatMap(column =>
                column.tasks.map(task => ({
                  ...task,
                  columnId: column.id,
                  columnTitle: column.title,
                  columnColor: column.color
                }))
              );

              // Filter tasks based on search and filters
              const filteredTasks = allTasks.filter(task => {
                // Search filter
                const searchLower = searchQuery.toLowerCase();
                const matchesSearch = !searchQuery ||
                  task.title.toLowerCase().includes(searchLower) ||
                  getDescriptionText(task.description).toLowerCase().includes(searchLower) ||
                  task.tags.some(tag => tag.toLowerCase().includes(searchLower));

                if (!matchesSearch) return false;

                // Tag filter
                if (filters.tags.length > 0 && !filters.tags.some(tag => task.tags.includes(tag))) {
                  return false;
                }

                // Priority filter
                if (filters.priority.length > 0 && (!task.priority || !filters.priority.includes(task.priority))) {
                  return false;
                }

                // Date filter
                if (filters.dateFrom && task.createdAt) {
                  const taskDate = new Date(task.createdAt);
                  const fromDate = new Date(filters.dateFrom);
                  if (taskDate < fromDate) return false;
                }

                if (filters.dateTo && task.createdAt) {
                  const taskDate = new Date(task.createdAt);
                  const toDate = new Date(filters.dateTo);
                  if (taskDate > toDate) return false;
                }

                return true;
              });

              // Sort tasks - default to created date descending
              const sortField = sortConfig.field || 'created';
              const sortDirection = sortConfig.direction || 'desc';

              const sortedTasks = [...filteredTasks].sort((a, b) => {
                let comparison = 0;

                if (sortField === 'title') {
                  comparison = a.title.localeCompare(b.title);
                } else if (sortField === 'priority') {
                  const priorityOrder = { highest: 5, high: 4, medium: 3, low: 2, lowest: 1 };
                  const aPriority = priorityOrder[a.priority || 'medium'];
                  const bPriority = priorityOrder[b.priority || 'medium'];
                  comparison = bPriority - aPriority;
                } else if (sortField === 'created') {
                  const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                  const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                  comparison = bDate - aDate;
                }

                return sortDirection === 'asc' ? -comparison : comparison;
              });

              // Group tasks based on sort field
              const groupedTasks: { [key: string]: typeof sortedTasks } = {};

              sortedTasks.forEach(task => {
                let groupKey = '';

                if (sortField === 'title') {
                  groupKey = task.title.charAt(0).toUpperCase();
                } else if (sortField === 'priority') {
                  groupKey = (task.priority || 'medium').charAt(0).toUpperCase() + (task.priority || 'medium').slice(1);
                } else if (sortField === 'created') {
                  if (task.createdAt) {
                    const date = new Date(task.createdAt);
                    groupKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  } else {
                    groupKey = 'No Date';
                  }
                }

                if (!groupedTasks[groupKey]) {
                  groupedTasks[groupKey] = [];
                }
                groupedTasks[groupKey].push(task);
              });

              const colStyle = (col: ListColKey): React.CSSProperties =>
                col === 'summary'
                  ? { flex: 1, minWidth: 0 }
                  : { width: listColWidths[col], flexShrink: 0 };

              const renderHeaderCell = (col: ListColKey) => {
                const isDragTarget = colDragOver === col;
                const base: React.CSSProperties = { ...colStyle(col), position: 'relative', outline: isDragTarget ? '2px solid #6366f1' : 'none' };
                switch (col) {
                  case 'summary': return (
                    <div key={col} className="list-col list-col-summary" style={base} draggable onDragStart={() => startColDrag(col)} onDragOver={(e) => onColDragOver(e, col)} onDrop={() => onColDrop(col)}>
                      <span className="list-col-hdr-aa">Aa</span><span>Summary</span>
                      <span className="col-resize-handle" onMouseDown={(e) => { e.stopPropagation(); }} />
                    </div>
                  );
                  case 'tags': return (
                    <div key={col} className="list-col list-col-tags" style={base} draggable onDragStart={() => startColDrag(col)} onDragOver={(e) => onColDragOver(e, col)} onDrop={() => onColDrop(col)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
                      <span>Tags</span>
                      <span className="col-resize-handle" onMouseDown={(e) => startColResize(e, col)} />
                    </div>
                  );
                  case 'roadmap': return (
                    <div key={col} className="list-col list-col-roadmap" style={base} draggable onDragStart={() => startColDrag(col)} onDragOver={(e) => onColDragOver(e, col)} onDrop={() => onColDrop(col)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                      <span>Roadmap</span>
                      <span className="col-resize-handle" onMouseDown={(e) => startColResize(e, col)} />
                    </div>
                  );
                  case 'subtasks': return (
                    <div key={col} className="list-col list-col-subtasks" style={base} draggable onDragStart={() => startColDrag(col)} onDragOver={(e) => onColDragOver(e, col)} onDrop={() => onColDrop(col)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                      <span>Priority</span>
                      <span className="col-resize-handle" onMouseDown={(e) => startColResize(e, col)} />
                    </div>
                  );
                  case 'comments': return (
                    <div key={col} className="list-col list-col-comments" style={base} draggable onDragStart={() => startColDrag(col)} onDragOver={(e) => onColDragOver(e, col)} onDrop={() => onColDrop(col)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                      <span>Assignee</span>
                      <span className="col-resize-handle" onMouseDown={(e) => startColResize(e, col)} />
                    </div>
                  );
                  case 'actions': return (
                    <div key={col} className="list-col list-col-actions" style={base} draggable onDragStart={() => startColDrag(col)} onDragOver={(e) => onColDragOver(e, col)} onDrop={() => onColDrop(col)}>
                      <span className="list-hdr-actions-label">Actions</span>
                    </div>
                  );
                  default: return null;
                }
              };

              const renderRowCell = (col: ListColKey, task: Task & { columnId?: string; columnColor?: string }) => {
                switch (col) {
                  case 'summary': return (
                    <div key={col} className="list-col list-col-summary" style={colStyle(col)}>
                      {task.priority === 'highest' && <PriorityHighestIcon width="14" height="14" className="list-view-priority-icon" />}
                      {task.priority === 'high' && <PriorityHighIcon width="14" height="14" className="list-view-priority-icon" />}
                      {task.priority === 'medium' && <PriorityMediumIcon width="14" height="14" className="list-view-priority-icon" />}
                      {task.priority === 'low' && <PriorityLowIcon width="14" height="14" className="list-view-priority-icon" />}
                      {task.priority === 'lowest' && <PriorityLowestIcon width="14" height="14" className="list-view-priority-icon" />}
                      <span className="list-row-title">{task.title}</span>
                    </div>
                  );
                  case 'tags': return (
                    <div key={col} className="list-col list-col-tags" style={colStyle(col)}>
                      {task.tags && task.tags.length > 0 ? <span className={`list-tag-chip list-tag-chip-${(task.tags[0].charCodeAt(0) % 5) + 1}`}>{task.tags[0]}</span> : <span className="list-col-empty">{'-'}</span>}
                    </div>
                  );
                  case 'roadmap': return (
                    <div key={col} className="list-col list-col-roadmap" style={colStyle(col)}>
                      <select className="list-status-select" style={{ backgroundColor: (task.columnColor ?? '') + '20', color: task.columnColor ?? '' }} value={task.columnId ?? ''} onClick={(e) => e.stopPropagation()} onChange={(e) => { e.stopPropagation(); handleMoveTask(task.id, task.columnId ?? '', e.target.value); }}>
                        {columns.map(col2 => <option key={col2.id} value={col2.id}>{col2.title}</option>)}
                      </select>
                    </div>
                  );
                  case 'subtasks': return (
                    <div key={col} className="list-col list-col-subtasks" style={colStyle(col)}>
                      {task.priority ? <div className="list-priority-wrap">{task.priority === 'highest' && <PriorityHighestIcon width="14" height="14" />}{task.priority === 'high' && <PriorityHighIcon width="14" height="14" />}{task.priority === 'medium' && <PriorityMediumIcon width="14" height="14" />}{task.priority === 'low' && <PriorityLowIcon width="14" height="14" />}{task.priority === 'lowest' && <PriorityLowestIcon width="14" height="14" />}<span className="list-priority-label">{task.priority}</span></div> : <span className="list-col-empty">{'-'}</span>}
                    </div>
                  );
                  case 'comments': return (
                    <div key={col} className="list-col list-col-comments" style={colStyle(col)}>
                      {task.assignee && task.assignee !== 'Unassigned' ? <div className="list-assignee-wrap"><div className="task-assignee-avatar" style={{ background: `hsl(${(task.assignee.charCodeAt(0) * 37) % 360}, 60%, 45%)` }}>{task.assignee.charAt(0).toUpperCase()}</div><span className="list-assignee-tooltip">{task.assignee}</span></div> : <span className="list-col-empty">{'-'}</span>}
                    </div>
                  );
                  case 'actions': return (
                    <div key={col} className="list-col list-col-actions" style={colStyle(col)}>
                      <button className="list-view-edit-btn" onClick={(e) => { e.stopPropagation(); handleEditTask(task.columnId ?? '', task.id); }} title="Edit task">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                      </button>
                      <button className="list-view-delete-btn" onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.columnId ?? '', task.id); }} title="Delete task">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                      </button>
                    </div>
                  );
                  default: return null;
                }
              };

              return (
                <div className="list-view-table">
                  {/* Table column headers — draggable to reorder, resize handle on right edge */}
                  <div className="list-table-header">
                    {listColOrder.map(col => renderHeaderCell(col))}
                  </div>
                  {Object.entries(groupedTasks).map(([groupName, tasks]) => (
                    <div key={groupName} className="list-view-group">
                      <div className="list-view-group-header">
                        <h3 className="list-view-group-title">{groupName}</h3>
                        <span className="list-view-group-count">{tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}</span>
                      </div>
                      {tasks.map(task => (
                        <div key={task.id} className="list-table-row" onClick={() => handleEditTask(task.columnId, task.id)}>
                          {listColOrder.map(col => renderRowCell(col, task))}
                        </div>
                      ))}
                    </div>
                  ))}

                  {sortedTasks.length === 0 && (
                    <div className="list-view-empty">
                      <p>No tasks found</p>
                    </div>
                  )}
                </div>
              );
            })()}
            </div>
      )}

      {viewMode === 'calendar' && (
        <TaskCalendarView
          columns={columns}
          tasks={(() => {
            // Same search + tag + priority predicate the list view uses, so
            // the three views always agree on which tasks are in scope.
            // The date filter is deliberately skipped here: it filters on
            // createdAt, which would fight the calendar's own date axis.
            const searchLower = searchQuery.toLowerCase();
            return columns.flatMap(column =>
              column.tasks
                .filter(task => {
                  const matchesSearch = !searchQuery ||
                    task.title.toLowerCase().includes(searchLower) ||
                    getDescriptionText(task.description).toLowerCase().includes(searchLower) ||
                    task.tags.some(tag => tag.toLowerCase().includes(searchLower));
                  if (!matchesSearch) return false;

                  if (filters.tags.length > 0 && !filters.tags.some(tag => task.tags.includes(tag))) {
                    return false;
                  }
                  if (filters.priority.length > 0 &&
                      (!task.priority || !filters.priority.includes(task.priority))) {
                    return false;
                  }
                  return true;
                })
                .map<CalendarTask>(task => ({
                  ...task,
                  columnId: column.id,
                  columnTitle: column.title,
                  columnColor: column.color,
                }))
            );
          })()}
          onTaskClick={handleEditTask}
        />
      )}

      {/* Task Form Backdrop - rendered before modal so modal paints on top */}
      {(editingTask !== null || showAddForm !== null) && (
        <div className="task-form-backdrop" onClick={handleCancelAddTask} />
      )}

      {/* Redesigned Edit Task Modal */}
      {editingTask && (
        <div className="inline-edit-form premium-task-modal" ref={editFormRef}>
          <div className="task-modal-header">
            <div className="header-titles-new">
              <h3>Edit Task</h3>
              <p className="header-subtitle-new">Update task details and manage workflow</p>
            </div>
            <button type="button" className="task-modal-close" onClick={handleCancelAddTask}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div className="task-modal-content-layout">
            {/* LEFT COLUMN */}
            <div className="task-modal-main-column">
              {/* Title input field */}
              <div className="form-group-new">
                <label className="section-label-new">Title <span style={{ color: '#dc2626' }}>*</span></label>
                <div className="title-input-wrapper-new">
                  <input
                    type="text"
                    className="task-title-input-new"
                    placeholder="Enter task title"
                    value={newTaskData.title}
                    onChange={(e) => setNewTaskData({ ...newTaskData, title: e.target.value })}
                    autoFocus
                    required
                  />
                  <span className="title-ai-badge-new" title="AI Generate Title">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                  </span>
                </div>
              </div>

              {/* Description — @editablejs rich text editor */}
              <div className="form-group-new">
                <label className="section-label-new">Description <span style={{ color: '#dc2626' }}>*</span></label>
                <TaskDescriptionEditor
                  key={'edit-' + (editingTask ? editingTask.taskId : 'none')}
                  htmlValue={(() => {
                    // Read directly from columns (ground truth) to avoid startTransition lag
                    if (editingTask) {
                      const col = columns.find(c => c.id === editingTask.columnId);
                      const task = col?.tasks.find(t => t.id === editingTask.taskId);
                      if (task?.description) return task.description;
                    }
                    return newTaskData.description;
                  })()}
                  onChange={(html) => setNewTaskData(prev => ({ ...prev, description: html }))}
                  placeholder="Add a clear and detailed description..."
                />
              </div>

              {/* INTEGRATIONS BUTTONS PANEL */}
              <div className="form-group-new">
                <div className="integration-buttons-grid-new">
                  {/* Row 1: Docs, Drive, Figma, Jira, Image */}
                  <div className="integrations-grid-row-new">
                    <button
                      type="button"
                      className={`integration-btn-new ${showDocsField ? 'active' : ''}`}
                      onClick={() => setShowDocsField(!showDocsField)}
                    >
                      <GoogleDocsIcon width="14" height="14" />
                      Docs
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showDriveField ? 'active' : ''}`}
                      onClick={() => setShowDriveField(!showDriveField)}
                    >
                      <GoogleDriveIcon width="14" height="14" />
                      Drive
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showFigmaField ? 'active' : ''}`}
                      onClick={() => setShowFigmaField(!showFigmaField)}
                    >
                      <FigmaIcon width="14" height="14" />
                      Figma
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showJiraField ? 'active' : ''}`}
                      onClick={() => setShowJiraField(!showJiraField)}
                    >
                      <JiraIcon width="14" height="14" />
                      Jira
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showImageField ? 'active' : ''}`}
                      onClick={() => setShowImageField(!showImageField)}
                    >
                      <ImageIcon width="14" height="14" />
                      Image
                    </button>
                  </div>

                  {/* Row 2: Tags, Task, URL, Reminder, Notes */}
                  <div className="integrations-grid-row-new">
                    <button
                      type="button"
                      className={`integration-btn-new ${showTagsField ? 'active' : ''}`}
                      onClick={() => setShowTagsField(!showTagsField)}
                    >
                      <TagsIcon width="14" height="14" />
                      Tags
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showSubtasksField ? 'active' : ''}`}
                      onClick={() => setShowSubtasksField(!showSubtasksField)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="8" y1="6" x2="21" y2="6"></line>
                        <line x1="8" y1="12" x2="21" y2="12"></line>
                        <line x1="8" y1="18" x2="21" y2="18"></line>
                        <line x1="3" y1="6" x2="3.01" y2="6"></line>
                        <line x1="3" y1="12" x2="3.01" y2="12"></line>
                        <line x1="3" y1="18" x2="3.01" y2="18"></line>
                      </svg>
                      Task
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showUrlField ? 'active' : ''}`}
                      onClick={() => setShowUrlField(!showUrlField)}
                    >
                      <URLIcon width="14" height="14" />
                      URL
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showReminderField ? 'active' : ''}`}
                      onClick={() => setShowReminderField(!showReminderField)}
                    >
                      <ReminderIcon width="14" height="14" />
                      Reminder
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showNotesField ? 'active' : ''}`}
                      onClick={() => setShowNotesField(!showNotesField)}
                    >
                      <NotesIcon width="14" height="14" />
                      Notes
                    </button>
                  </div>
                </div>
              </div>

              {/* CONDITIONAL INTEGRATION INPUTS */}
              {showDocsField && (
                <div className="form-group-new docs-group-new">
                  <label className="section-label-new">
                    <GoogleDocsIcon width="14" height="14" style={{marginRight: '6px', verticalAlign: 'middle'}} />
                    Google Docs Links (Max 3)
                  </label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.docs.map((doc, index) => (
                        <span key={index} className="tag-item link-tag-item">
                          <span className="link-text-short">{doc.length > 40 ? doc.substring(0, 40) + '...' : doc}</span>
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveDocs(doc)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Paste Google Docs, Sheets, or Slides link..."
                        value={currentDocs}
                        onChange={(e) => setCurrentDocs(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDocs(); } }}
                        disabled={newTaskData.docs.length >= 3}
                      />
                      <button type="button" onClick={handleAddDocs} className="tag-add-btn" disabled={newTaskData.docs.length >= 3}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showDriveField && (
                <div className="form-group-new drive-group-new">
                  <label className="section-label-new">
                    <GoogleDriveIcon width="14" height="14" style={{marginRight: '6px', verticalAlign: 'middle'}} />
                    Google Drive Links (Max 3)
                  </label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.drive.map((link, index) => (
                        <span key={index} className="tag-item link-tag-item">
                          <span className="link-text-short">{link.length > 40 ? link.substring(0, 40) + '...' : link}</span>
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveDrive(link)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Paste Google Drive link..."
                        value={currentDrive}
                        onChange={(e) => setCurrentDrive(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDrive(); } }}
                        disabled={newTaskData.drive.length >= 3}
                      />
                      <button type="button" onClick={handleAddDrive} className="tag-add-btn" disabled={newTaskData.drive.length >= 3}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showFigmaField && (
                <div className="form-group-new figma-group-new">
                  <label className="section-label-new">
                    <FigmaIcon width="14" height="14" style={{marginRight: '6px', verticalAlign: 'middle'}} />
                    Figma Design Links (Max 3)
                  </label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.figma.map((link, index) => (
                        <span key={index} className="tag-item link-tag-item">
                          <span className="link-text-short">{link.length > 40 ? link.substring(0, 40) + '...' : link}</span>
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveFigma(link)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Paste Figma file or frame link..."
                        value={currentFigma}
                        onChange={(e) => setCurrentFigma(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddFigma(); } }}
                        disabled={newTaskData.figma.length >= 3}
                      />
                      <button type="button" onClick={handleAddFigma} className="tag-add-btn" disabled={newTaskData.figma.length >= 3}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showJiraField && (
                <div className="form-group-new jira-group-new">
                  <label className="section-label-new">
                    <JiraIcon width="14" height="14" style={{marginRight: '6px', verticalAlign: 'middle'}} />
                    Jira Ticket Links (Max 3)
                  </label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.jira.map((link, index) => (
                        <span key={index} className="tag-item link-tag-item">
                          <span className="link-text-short">{link.length > 40 ? link.substring(0, 40) + '...' : link}</span>
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveJira(link)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Paste Jira ticket URL..."
                        value={currentJira}
                        onChange={(e) => setCurrentJira(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddJira(); } }}
                        disabled={newTaskData.jira.length >= 3}
                      />
                      <button type="button" onClick={handleAddJira} className="tag-add-btn" disabled={newTaskData.jira.length >= 3}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showImageField && (
                <div className="form-group-new image-group-new">
                  <label className="section-label-new">Choose Image Attachment</label>
                  <div className="attachments-dropzone-new" onClick={() => document.getElementById('add-attachments-file-input')?.click()}>
                    <p className="dropzone-text-new">Click to <span className="browse-link-new">browse</span> and upload your image</p>
                  </div>
                </div>
              )}

              {showTagsField && (
                <div className="form-group-new tags-group-new">
                  <label className="section-label-new">Tags</label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.tags.map((tag, index) => (
                        <span key={index} className={`tag-item ${tag.toLowerCase()}`}>
                          {tag}
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveTag(tag)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Type tag and press Enter..."
                        value={currentTag}
                        onChange={(e) => setCurrentTag(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                      />
                      <button type="button" onClick={handleAddTag} className="tag-add-btn">Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showUrlField && (
                <div className="form-group-new url-group-new">
                  <label className="section-label-new">
                    <URLIcon width="14" height="14" style={{marginRight: '6px', verticalAlign: 'middle'}} />
                    External URLs (Max 3)
                  </label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.url.map((link, index) => (
                        <span key={index} className="tag-item link-tag-item">
                          <span className="link-text-short">{link.length > 40 ? link.substring(0, 40) + '...' : link}</span>
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveUrl(link)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Paste website URL..."
                        value={currentUrl}
                        onChange={(e) => setCurrentUrl(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddUrl(); } }}
                        disabled={newTaskData.url.length >= 3}
                      />
                      <button type="button" onClick={handleAddUrl} className="tag-add-btn" disabled={newTaskData.url.length >= 3}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showReminderField && (
                <div className="form-group-new reminder-group-new">
                  <label className="section-label-new">Set Reminder</label>
                  <input
                    type="datetime-local"
                    value={newTaskData.reminder}
                    onChange={(e) => setNewTaskData({ ...newTaskData, reminder: e.target.value })}
                    className="task-input-sidebar"
                    style={{ height: '36px', fontSize: '13px', borderRadius: '8px' }}
                  />
                </div>
              )}

              {showNotesField && (
                <div className="form-group-new notes-group-new">
                  <label className="section-label-new">Notes</label>
                  <textarea
                    placeholder="Add notes here..."
                    value={newTaskData.notes}
                    onChange={(e) => setNewTaskData({ ...newTaskData, notes: e.target.value })}
                    rows={3}
                    className="task-notes-textarea"
                  />
                </div>
              )}

              {/* Attachments dashed dropzone */}
              <div className="form-group-new">
                <label className="section-label-new">Attachments</label>
                <div className="attachments-dropzone-new" onClick={() => document.getElementById('edit-attachments-file-input')?.click()}>
                  <input
                    type="file"
                    id="edit-attachments-file-input"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleAttachmentUpload(file);
                      }
                    }}
                  />
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="dropzone-paperclip-new">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                  {isUploadingAttachment ? <p className="dropzone-text-new">Uploading file to storage... <span className="browse-link-new" style={{color: "#3b82f6"}}>Please wait</span></p> : <p className="dropzone-text-new">Drag and drop files here or <span className="browse-link-new">browse</span></p>}
                  <p className="dropzone-subtext-new">Supports images, documents, links (Max 10MB)</p>
                </div>
                {newTaskData.image && (
                  <div className="image-preview-container-new">
                    <img src={newTaskData.image} alt="Preview" className="image-preview-img-new" />
                    <button
                      type="button"
                      className="image-remove-btn-new"
                      onClick={() => setNewTaskData({ ...newTaskData, image: '' })}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              {/* Sub-tasks Section */}
              {showSubtasksField && (
              <div className="form-group-new">
                <label className="section-label-new">Sub-tasks</label>
                <div className="subtasks-container-new">
                  {newTaskData.subtasks.map((subtask) => (
                    <div key={subtask.id} className="subtask-row-new">
                      <input
                        type="checkbox"
                        checked={subtask.completed}
                        onChange={() => handleToggleSubtask(subtask.id)}
                        className="subtask-checkbox-new"
                      />
                      <span className={`subtask-text-new ${subtask.completed ? 'completed' : ''}`}>
                        {subtask.text}
                      </span>
                      <button
                        type="button"
                        className="subtask-remove-btn-new"
                        onClick={() => handleRemoveSubtask(subtask.id)}
                        title="Remove subtask"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  ))}
                  {/* Inline Subtask input row */}
                  <div className="subtask-input-row-new">
                    <input
                      type="text"
                      placeholder="Type subtask text..."
                      value={currentSubtask}
                      onChange={(e) => setCurrentSubtask(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSubtask();
                        }
                      }}
                      className="subtask-input-new"
                    />
                    <button
                      type="button"
                      className="subtask-add-btn-new"
                      onClick={handleAddSubtask}
                    >
                      + Add sub-task
                    </button>
                  </div>
                </div>
              </div>
              )}

              {/* Comments + History tabbed section */}
              <div className="form-group-new comments-group-new">
                {/* Tab bar */}
                <div className="comment-tabs-bar">
                  <button
                    type="button"
                    className={`comment-tab-btn ${commentTab === 'comments' ? 'active' : ''}`}
                    onClick={() => setCommentTab('comments')}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    Comments
                    {taskComments.length > 0 && <span className="comment-tab-badge">{taskComments.length}</span>}
                  </button>
                  <button
                    type="button"
                    className={`comment-tab-btn ${commentTab === 'history' ? 'active' : ''}`}
                    onClick={() => setCommentTab('history')}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    History
                    {taskHistory.length > 0 && <span className="comment-tab-badge">{taskHistory.length}</span>}
                  </button>
                </div>

                {/* Comments tab content */}
                {commentTab === 'comments' && (
                  <>
                    {taskComments.length > 0 && (
                      <div className="comments-feed-new">
                        {[...taskComments].reverse().map(comment => (
                          <div key={comment.id} className="comment-card-new">
                            <div className="comment-card-header-new">
                              <div className="comment-avatar-new">{comment.authorName?.charAt(0)?.toUpperCase() || 'U'}</div>
                              <div className="comment-meta-new">
                                <span className="comment-author-name-new">{comment.authorName}</span>
                                <span className="comment-dot-new">•</span>
                                <span className="comment-time-new">
                                  {new Date(comment.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                            <div className="comment-body-new">{comment.text}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {taskComments.length === 0 && (
                      <div className="comment-empty-state">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        <span>No comments yet</span>
                      </div>
                    )}
                    {/* Add Comment Input */}
                    <div className="comment-input-row-new">
                      <div className="comment-avatar-new">{commentAuthorName?.charAt(0)?.toUpperCase() || 'U'}</div>
                      <div className="comment-input-box-wrapper-new">
                        <textarea
                          ref={commentTextareaRef}
                          className="comment-textarea-new"
                          placeholder="Add a comment... type @ to mention someone"
                          value={commentInput}
                          onChange={handleCommentInputChange}
                          rows={1}
                          onKeyDown={(e) => { if (e.key === 'Escape') setShowMentionDropdown(false); }}
                        />
                        {showMentionDropdown && orgMembers.length > 0 && (
                          <div className="mention-dropdown-new">
                            {orgMembers.filter(m => m.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 6).map(member => (
                              <div key={member.id} className="mention-option-new" onMouseDown={(e) => { e.preventDefault(); insertMention(member.name); }}>
                                <span className="mention-avatar-new">{member.name.charAt(0).toUpperCase()}</span>
                                {member.name}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="comment-input-actions-new">
                          <span className="comment-emoji-btn-new" title="Add Emoji">😊</span>
                          <button type="button" className="comment-send-btn-new" onClick={handleAddComment} disabled={!commentInput.trim()} title="Send Comment">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* History tab content */}
                {commentTab === 'history' && (
                  <div className="history-feed">
                    {taskHistory.length === 0 ? (
                      <div className="comment-empty-state">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        <span>No history yet</span>
                      </div>
                    ) : (
                      taskHistory.map(entry => (
                        <div key={entry.id} className="history-entry">
                          <div className="history-dot" />
                          <div className="history-content">
                            <span className="history-action">{entry.action}</span>
                            <div className="history-meta">
                              <span className="history-user">{entry.user}</span>
                              <span className="history-sep">•</span>
                              <span className="history-time">
                                {new Date(entry.time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="task-modal-sidebar-column">
              {activeDropdown && (
                <div className="custom-dropdown-backdrop" onClick={() => setActiveDropdown(null)} />
              )}


              {/* Priority */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Priority</label>
                <div className={`sidebar-select-wrapper-custom-new ${activeDropdown === 'priority' ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="sidebar-select-trigger-new has-indicator-new"
                    onClick={() => setActiveDropdown(activeDropdown === 'priority' ? null : 'priority')}
                  >
                    <span className={`priority-indicator-dot-new ${newTaskData.priority}`} />
                    <span style={{textTransform: 'capitalize'}}>{newTaskData.priority || 'Medium'}</span>
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {activeDropdown === 'priority' && (
                    <ul className="sidebar-dropdown-menu-new">
                      {['lowest', 'low', 'medium', 'high', 'highest'].map((p) => (
                        <li
                          key={p}
                          className={`sidebar-dropdown-item-new ${newTaskData.priority === p ? 'selected' : ''}`}
                          onClick={() => {
                            setNewTaskData({ ...newTaskData, priority: p as any });
                            setActiveDropdown(null);
                          }}
                        >
                          <span className={`priority-indicator-dot-new ${p}`} style={{position: 'static', transform: 'none'}} />
                          <span style={{textTransform: 'capitalize'}}>{p}</span>
                          {newTaskData.priority === p && <span className="dropdown-item-check-new">✓</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Assignee */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Assignee</label>
                <div className={`sidebar-select-wrapper-custom-new ${activeDropdown === 'assignee' ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="sidebar-select-trigger-new has-icon-new"
                    onClick={() => setActiveDropdown(activeDropdown === 'assignee' ? null : 'assignee')}
                  >
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" style={{flexShrink: 0}}>
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <span>{newTaskData.assignee || 'Unassigned'}</span>
                    </div>
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {activeDropdown === 'assignee' && (
                    <ul className="sidebar-dropdown-menu-new">
                      <li
                        className={`sidebar-dropdown-item-new ${newTaskData.assignee === 'Unassigned' ? 'selected' : ''}`}
                        onClick={() => {
                          setNewTaskData({ ...newTaskData, assignee: 'Unassigned' });
                          setActiveDropdown(null);
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" style={{flexShrink: 0}}>
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                        <span>Unassigned</span>
                        {newTaskData.assignee === 'Unassigned' && <span className="dropdown-item-check-new">✓</span>}
                      </li>
                      {orgMembers.map(member => (
                        <li
                          key={member.id}
                          className={`sidebar-dropdown-item-new ${newTaskData.assignee === member.name ? 'selected' : ''}`}
                          onClick={() => {
                            setNewTaskData({ ...newTaskData, assignee: member.name });
                            setActiveDropdown(null);
                          }}
                        >
                          <span className="member-avatar-mini" style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: '#e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            color: '#475569',
                            flexShrink: 0
                          }}>
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                          <span>{member.name}</span>
                          {newTaskData.assignee === member.name && <span className="dropdown-item-check-new">✓</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Labels */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Labels</label>
                <div className={`sidebar-select-wrapper-custom-new ${activeDropdown === 'labels' ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="sidebar-select-trigger-new"
                    onClick={() => setActiveDropdown(activeDropdown === 'labels' ? null : 'labels')}
                  >
                    <span>{newTaskData.tags.length > 0 ? newTaskData.tags.join(', ') : 'Select labels'}</span>
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {activeDropdown === 'labels' && (
                    <ul className="sidebar-dropdown-menu-new">
                      {['Bug', 'Feature', 'Design', 'Documentation'].map(tag => {
                        const isSelected = newTaskData.tags.includes(tag);
                        return (
                          <li
                            key={tag}
                            className={`sidebar-dropdown-item-new ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              const updatedTags = isSelected
                                ? newTaskData.tags.filter(t => t !== tag)
                                : [...newTaskData.tags, tag];
                              setNewTaskData({ ...newTaskData, tags: updatedTags });
                            }}
                          >
                            <span className={`tag-color-indicator ${tag.toLowerCase()}`} style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: tag === 'Bug' ? '#ef4444' : tag === 'Feature' ? '#3b82f6' : tag === 'Design' ? '#a855f7' : '#10b981',
                              flexShrink: 0
                            }} />
                            <span>{tag}</span>
                            {isSelected && <span className="dropdown-item-check-new">✓</span>}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Status</label>
                <div className={`sidebar-select-wrapper-custom-new ${activeDropdown === 'status' ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="sidebar-select-trigger-new"
                    onClick={() => setActiveDropdown(activeDropdown === 'status' ? null : 'status')}
                  >
                    <span>
                      {columns.find(col => col.id === (newTaskData.status || editingTask.columnId))?.title || 'Select status'}
                    </span>
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {activeDropdown === 'status' && (
                    <ul className="sidebar-dropdown-menu-new">
                      {columns.map(col => {
                        const currentVal = newTaskData.status || editingTask.columnId;
                        const isSelected = currentVal === col.id;
                        return (
                          <li
                            key={col.id}
                            className={`sidebar-dropdown-item-new ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              setNewTaskData({ ...newTaskData, status: col.id });
                              setActiveDropdown(null);
                            }}
                          >
                            <span>{col.title}</span>
                            {isSelected && <span className="dropdown-item-check-new">✓</span>}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
              <div className="extra-sidebar-fields-new">
                  {/* Due Date */}
                  <div className="form-group-sidebar-new">
                    <label className="sidebar-label-new">Due Date</label>
                    <div className="sidebar-select-wrapper-new">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" className="sidebar-icon-new">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <input
                        type="date"
                        value={newTaskData.dueDate}
                        onChange={(e) => setNewTaskData({ ...newTaskData, dueDate: e.target.value })}
                        className="sidebar-select-new has-icon-new date-picker-new"
                      />
                    </div>
                  </div>

                  {/* Start Date */}
                  <div className="form-group-sidebar-new">
                    <label className="sidebar-label-new">Start Date</label>
                    <div className="sidebar-select-wrapper-new">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" className="sidebar-icon-new">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <input
                        type="date"
                        value={newTaskData.startDate}
                        onChange={(e) => setNewTaskData({ ...newTaskData, startDate: e.target.value })}
                        className="sidebar-select-new has-icon-new date-picker-new"
                      />
                    </div>
                  </div>

                  {/* Sprint */}
                  <div className="form-group-sidebar-new">
                    <label className="sidebar-label-new">Sprint</label>
                    <div className="sidebar-select-wrapper-new">
                      <select
                        value={newTaskData.sprint}
                        onChange={(e) => setNewTaskData({ ...newTaskData, sprint: e.target.value })}
                        className="sidebar-select-new"
                      >
                        <option value="">Select sprint</option>
                        <option value="Sprint 1">Sprint 1</option>
                        <option value="Sprint 2">Sprint 2</option>
                        <option value="Sprint 3">Sprint 3</option>
                      </select>
                    </div>
                  </div>

                  {/* Story Points */}
                  <div className="form-group-sidebar-new">
                    <label className="sidebar-label-new">Story Points</label>
                    <div className="story-points-input-wrapper-new">
                      <input
                        type="text"
                        placeholder="Enter story points"
                        value={newTaskData.storyPoints}
                        onChange={(e) => setNewTaskData({ ...newTaskData, storyPoints: e.target.value })}
                        className="story-points-input-new"
                      />
                      <span className="story-points-suffix-new">pts</span>
                    </div>
                  </div>

                  {/* Reporter */}
                  <div className="form-group-sidebar-new">
                    <label className="sidebar-label-new">Reporter</label>
                    <div className="sidebar-select-wrapper-new">
                      <div className="comment-avatar-new sidebar-avatar-new">AS</div>
                      <select
                        value={newTaskData.reporter}
                        onChange={(e) => setNewTaskData({ ...newTaskData, reporter: e.target.value })}
                        className="sidebar-select-new has-avatar-new"
                      >
                        <option value="Akash Sharma">Akash Sharma</option>
                        {orgMembers.map(member => (
                          <option key={member.id} value={member.name}>{member.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
            </div>
          </div>

          <div className="task-modal-footer">
            <button
              type="button"
              className="submit-btn"
              onClick={() => handleUpdateTask(editingTask!.columnId)}
            >
              Update Task
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancelAddTask}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Redesigned Add Task Modal */}
      {showAddForm && !editingTask && (
        <div className="inline-edit-form premium-task-modal" ref={addFormRef}>
          <div className="task-modal-header">
            <div className="header-titles-new">
              <h3>Add New Task</h3>
              <p className="header-subtitle-new">Create a new task and add details to get started</p>
            </div>
            <button type="button" className="task-modal-close" onClick={handleCancelAddTask}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div className="task-modal-content-layout">
            {/* LEFT COLUMN */}
            <div className="task-modal-main-column">
              {/* Title input field */}
              <div className="form-group-new">
                <label className="section-label-new">Title <span style={{ color: '#dc2626' }}>*</span></label>
                <div className="title-input-wrapper-new">
                  <input
                    type="text"
                    className="task-title-input-new"
                    placeholder="Enter task title"
                    value={newTaskData.title}
                    onChange={(e) => setNewTaskData({ ...newTaskData, title: e.target.value })}
                    autoFocus
                    required
                  />
                  <span className="title-ai-badge-new" title="AI Generate Title">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                  </span>
                </div>
              </div>

              {/* Description — @editablejs rich text editor */}
              <div className="form-group-new">
                <label className="section-label-new">Description <span style={{ color: '#dc2626' }}>*</span></label>
                <TaskDescriptionEditor
                  key={'add-' + (showAddForm || 'new')}
                  htmlValue={newTaskData.description}
                  onChange={(html) => setNewTaskData(prev => ({ ...prev, description: html }))}
                  placeholder="Add a clear and detailed description..."
                />
              </div>

              {/* INTEGRATIONS BUTTONS PANEL */}
              <div className="form-group-new">
                <div className="integration-buttons-grid-new">
                  {/* Row 1: Docs, Drive, Figma, Jira, Image */}
                  <div className="integrations-grid-row-new">
                    <button
                      type="button"
                      className={`integration-btn-new ${showDocsField ? 'active' : ''}`}
                      onClick={() => setShowDocsField(!showDocsField)}
                    >
                      <GoogleDocsIcon width="14" height="14" />
                      Docs
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showDriveField ? 'active' : ''}`}
                      onClick={() => setShowDriveField(!showDriveField)}
                    >
                      <GoogleDriveIcon width="14" height="14" />
                      Drive
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showFigmaField ? 'active' : ''}`}
                      onClick={() => setShowFigmaField(!showFigmaField)}
                    >
                      <FigmaIcon width="14" height="14" />
                      Figma
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showJiraField ? 'active' : ''}`}
                      onClick={() => setShowJiraField(!showJiraField)}
                    >
                      <JiraIcon width="14" height="14" />
                      Jira
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showImageField ? 'active' : ''}`}
                      onClick={() => setShowImageField(!showImageField)}
                    >
                      <ImageIcon width="14" height="14" />
                      Image
                    </button>
                  </div>

                  {/* Row 2: Tags, Task, URL, Reminder, Notes */}
                  <div className="integrations-grid-row-new">
                    <button
                      type="button"
                      className={`integration-btn-new ${showTagsField ? 'active' : ''}`}
                      onClick={() => setShowTagsField(!showTagsField)}
                    >
                      <TagsIcon width="14" height="14" />
                      Tags
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showSubtasksField ? 'active' : ''}`}
                      onClick={() => setShowSubtasksField(!showSubtasksField)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="8" y1="6" x2="21" y2="6"></line>
                        <line x1="8" y1="12" x2="21" y2="12"></line>
                        <line x1="8" y1="18" x2="21" y2="18"></line>
                        <line x1="3" y1="6" x2="3.01" y2="6"></line>
                        <line x1="3" y1="12" x2="3.01" y2="12"></line>
                        <line x1="3" y1="18" x2="3.01" y2="18"></line>
                      </svg>
                      Task
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showUrlField ? 'active' : ''}`}
                      onClick={() => setShowUrlField(!showUrlField)}
                    >
                      <URLIcon width="14" height="14" />
                      URL
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showReminderField ? 'active' : ''}`}
                      onClick={() => setShowReminderField(!showReminderField)}
                    >
                      <ReminderIcon width="14" height="14" />
                      Reminder
                    </button>
                    <button
                      type="button"
                      className={`integration-btn-new ${showNotesField ? 'active' : ''}`}
                      onClick={() => setShowNotesField(!showNotesField)}
                    >
                      <NotesIcon width="14" height="14" />
                      Notes
                    </button>
                  </div>
                </div>
              </div>

              {/* CONDITIONAL INTEGRATION INPUTS */}
              {showDocsField && (
                <div className="form-group-new docs-group-new">
                  <label className="section-label-new">
                    <GoogleDocsIcon width="14" height="14" style={{marginRight: '6px', verticalAlign: 'middle'}} />
                    Google Docs Links (Max 3)
                  </label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.docs.map((doc, index) => (
                        <span key={index} className="tag-item link-tag-item">
                          <span className="link-text-short">{doc.length > 40 ? doc.substring(0, 40) + '...' : doc}</span>
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveDocs(doc)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Paste Google Docs, Sheets, or Slides link..."
                        value={currentDocs}
                        onChange={(e) => setCurrentDocs(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDocs(); } }}
                        disabled={newTaskData.docs.length >= 3}
                      />
                      <button type="button" onClick={handleAddDocs} className="tag-add-btn" disabled={newTaskData.docs.length >= 3}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showDriveField && (
                <div className="form-group-new drive-group-new">
                  <label className="section-label-new">
                    <GoogleDriveIcon width="14" height="14" style={{marginRight: '6px', verticalAlign: 'middle'}} />
                    Google Drive Links (Max 3)
                  </label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.drive.map((link, index) => (
                        <span key={index} className="tag-item link-tag-item">
                          <span className="link-text-short">{link.length > 40 ? link.substring(0, 40) + '...' : link}</span>
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveDrive(link)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Paste Google Drive link..."
                        value={currentDrive}
                        onChange={(e) => setCurrentDrive(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDrive(); } }}
                        disabled={newTaskData.drive.length >= 3}
                      />
                      <button type="button" onClick={handleAddDrive} className="tag-add-btn" disabled={newTaskData.drive.length >= 3}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showFigmaField && (
                <div className="form-group-new figma-group-new">
                  <label className="section-label-new">
                    <FigmaIcon width="14" height="14" style={{marginRight: '6px', verticalAlign: 'middle'}} />
                    Figma Design Links (Max 3)
                  </label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.figma.map((link, index) => (
                        <span key={index} className="tag-item link-tag-item">
                          <span className="link-text-short">{link.length > 40 ? link.substring(0, 40) + '...' : link}</span>
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveFigma(link)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Paste Figma file or frame link..."
                        value={currentFigma}
                        onChange={(e) => setCurrentFigma(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddFigma(); } }}
                        disabled={newTaskData.figma.length >= 3}
                      />
                      <button type="button" onClick={handleAddFigma} className="tag-add-btn" disabled={newTaskData.figma.length >= 3}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showJiraField && (
                <div className="form-group-new jira-group-new">
                  <label className="section-label-new">
                    <JiraIcon width="14" height="14" style={{marginRight: '6px', verticalAlign: 'middle'}} />
                    Jira Ticket Links (Max 3)
                  </label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.jira.map((link, index) => (
                        <span key={index} className="tag-item link-tag-item">
                          <span className="link-text-short">{link.length > 40 ? link.substring(0, 40) + '...' : link}</span>
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveJira(link)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Paste Jira ticket URL..."
                        value={currentJira}
                        onChange={(e) => setCurrentJira(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddJira(); } }}
                        disabled={newTaskData.jira.length >= 3}
                      />
                      <button type="button" onClick={handleAddJira} className="tag-add-btn" disabled={newTaskData.jira.length >= 3}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showImageField && (
                <div className="form-group-new image-group-new">
                  <label className="section-label-new">Choose Image Attachment</label>
                  <div className="attachments-dropzone-new" onClick={() => document.getElementById('add-attachments-file-input')?.click()}>
                    <p className="dropzone-text-new">Click to <span className="browse-link-new">browse</span> and upload your image</p>
                  </div>
                </div>
              )}

              {showTagsField && (
                <div className="form-group-new tags-group-new">
                  <label className="section-label-new">Tags</label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.tags.map((tag, index) => (
                        <span key={index} className={`tag-item ${tag.toLowerCase()}`}>
                          {tag}
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveTag(tag)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Type tag and press Enter..."
                        value={currentTag}
                        onChange={(e) => setCurrentTag(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                      />
                      <button type="button" onClick={handleAddTag} className="tag-add-btn">Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showUrlField && (
                <div className="form-group-new url-group-new">
                  <label className="section-label-new">
                    <URLIcon width="14" height="14" style={{marginRight: '6px', verticalAlign: 'middle'}} />
                    External URLs (Max 3)
                  </label>
                  <div className="tags-input-container">
                    <div className="tags-display">
                      {newTaskData.url.map((link, index) => (
                        <span key={index} className="tag-item link-tag-item">
                          <span className="link-text-short">{link.length > 40 ? link.substring(0, 40) + '...' : link}</span>
                          <button type="button" className="tag-remove-btn" onClick={() => handleRemoveUrl(link)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <input
                        type="text"
                        placeholder="Paste website URL..."
                        value={currentUrl}
                        onChange={(e) => setCurrentUrl(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddUrl(); } }}
                        disabled={newTaskData.url.length >= 3}
                      />
                      <button type="button" onClick={handleAddUrl} className="tag-add-btn" disabled={newTaskData.url.length >= 3}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {showReminderField && (
                <div className="form-group-new reminder-group-new">
                  <label className="section-label-new">Set Reminder</label>
                  <input
                    type="datetime-local"
                    value={newTaskData.reminder}
                    onChange={(e) => setNewTaskData({ ...newTaskData, reminder: e.target.value })}
                    className="task-input-sidebar"
                    style={{ height: '36px', fontSize: '13px', borderRadius: '8px' }}
                  />
                </div>
              )}

              {showNotesField && (
                <div className="form-group-new notes-group-new">
                  <label className="section-label-new">Notes</label>
                  <textarea
                    placeholder="Add notes here..."
                    value={newTaskData.notes}
                    onChange={(e) => setNewTaskData({ ...newTaskData, notes: e.target.value })}
                    rows={3}
                    className="task-notes-textarea"
                  />
                </div>
              )}

              {/* Attachments dashed dropzone */}
              <div className="form-group-new">
                <label className="section-label-new">Attachments</label>
                <div className="attachments-dropzone-new" onClick={() => document.getElementById('add-attachments-file-input')?.click()}>
                  <input
                    type="file"
                    id="add-attachments-file-input"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleAttachmentUpload(file);
                      }
                    }}
                  />
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="dropzone-paperclip-new">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                  {isUploadingAttachment ? <p className="dropzone-text-new">Uploading file to storage... <span className="browse-link-new" style={{color: "#3b82f6"}}>Please wait</span></p> : <p className="dropzone-text-new">Drag and drop files here or <span className="browse-link-new">browse</span></p>}
                  <p className="dropzone-subtext-new">Supports images, documents, links (Max 10MB)</p>
                </div>
                {newTaskData.image && (
                  <div className="image-preview-container-new">
                    <img src={newTaskData.image} alt="Preview" className="image-preview-img-new" />
                    <button
                      type="button"
                      className="image-remove-btn-new"
                      onClick={() => setNewTaskData({ ...newTaskData, image: '' })}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              {/* Sub-tasks Section */}
              {showSubtasksField && (
              <div className="form-group-new">
                <label className="section-label-new">Sub-tasks</label>
                <div className="subtasks-container-new">
                  {newTaskData.subtasks.map((subtask) => (
                    <div key={subtask.id} className="subtask-row-new">
                      <input
                        type="checkbox"
                        checked={subtask.completed}
                        onChange={() => handleToggleSubtask(subtask.id)}
                        className="subtask-checkbox-new"
                      />
                      <span className={`subtask-text-new ${subtask.completed ? 'completed' : ''}`}>
                        {subtask.text}
                      </span>
                      <button
                        type="button"
                        className="subtask-remove-btn-new"
                        onClick={() => handleRemoveSubtask(subtask.id)}
                        title="Remove subtask"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  ))}
                  {/* Inline Subtask input row */}
                  <div className="subtask-input-row-new">
                    <input
                      type="text"
                      placeholder="Type subtask text..."
                      value={currentSubtask}
                      onChange={(e) => setCurrentSubtask(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSubtask();
                        }
                      }}
                      className="subtask-input-new"
                    />
                    <button
                      type="button"
                      className="subtask-add-btn-new"
                      onClick={handleAddSubtask}
                    >
                      + Add sub-task
                    </button>
                  </div>
                </div>
              </div>
              )}
            </div>

            {/* RIGHT COLUMN */}
            <div className="task-modal-sidebar-column">
              {activeDropdown && (
                <div className="custom-dropdown-backdrop" onClick={() => setActiveDropdown(null)} />
              )}


              {/* Priority */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Priority</label>
                <div className={`sidebar-select-wrapper-custom-new ${activeDropdown === 'priority' ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="sidebar-select-trigger-new has-indicator-new"
                    onClick={() => setActiveDropdown(activeDropdown === 'priority' ? null : 'priority')}
                  >
                    <span className={`priority-indicator-dot-new ${newTaskData.priority}`} />
                    <span style={{textTransform: 'capitalize'}}>{newTaskData.priority || 'Medium'}</span>
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {activeDropdown === 'priority' && (
                    <ul className="sidebar-dropdown-menu-new">
                      {['lowest', 'low', 'medium', 'high', 'highest'].map((p) => (
                        <li
                          key={p}
                          className={`sidebar-dropdown-item-new ${newTaskData.priority === p ? 'selected' : ''}`}
                          onClick={() => {
                            setNewTaskData({ ...newTaskData, priority: p as any });
                            setActiveDropdown(null);
                          }}
                        >
                          <span className={`priority-indicator-dot-new ${p}`} style={{position: 'static', transform: 'none'}} />
                          <span style={{textTransform: 'capitalize'}}>{p}</span>
                          {newTaskData.priority === p && <span className="dropdown-item-check-new">✓</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Assignee */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Assignee</label>
                <div className={`sidebar-select-wrapper-custom-new ${activeDropdown === 'assignee' ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="sidebar-select-trigger-new has-icon-new"
                    onClick={() => setActiveDropdown(activeDropdown === 'assignee' ? null : 'assignee')}
                  >
                    <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" style={{flexShrink: 0}}>
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <span>{newTaskData.assignee || 'Unassigned'}</span>
                    </div>
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {activeDropdown === 'assignee' && (
                    <ul className="sidebar-dropdown-menu-new">
                      <li
                        className={`sidebar-dropdown-item-new ${newTaskData.assignee === 'Unassigned' ? 'selected' : ''}`}
                        onClick={() => {
                          setNewTaskData({ ...newTaskData, assignee: 'Unassigned' });
                          setActiveDropdown(null);
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" style={{flexShrink: 0}}>
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                        <span>Unassigned</span>
                        {newTaskData.assignee === 'Unassigned' && <span className="dropdown-item-check-new">✓</span>}
                      </li>
                      {orgMembers.map(member => (
                        <li
                          key={member.id}
                          className={`sidebar-dropdown-item-new ${newTaskData.assignee === member.name ? 'selected' : ''}`}
                          onClick={() => {
                            setNewTaskData({ ...newTaskData, assignee: member.name });
                            setActiveDropdown(null);
                          }}
                        >
                          <span className="member-avatar-mini" style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: '#e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            color: '#475569',
                            flexShrink: 0
                          }}>
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                          <span>{member.name}</span>
                          {newTaskData.assignee === member.name && <span className="dropdown-item-check-new">✓</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Labels */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Labels</label>
                <div className={`sidebar-select-wrapper-custom-new ${activeDropdown === 'labels' ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="sidebar-select-trigger-new"
                    onClick={() => setActiveDropdown(activeDropdown === 'labels' ? null : 'labels')}
                  >
                    <span>{newTaskData.tags.length > 0 ? newTaskData.tags.join(', ') : 'Select labels'}</span>
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {activeDropdown === 'labels' && (
                    <ul className="sidebar-dropdown-menu-new">
                      {['Bug', 'Feature', 'Design', 'Documentation'].map(tag => {
                        const isSelected = newTaskData.tags.includes(tag);
                        return (
                          <li
                            key={tag}
                            className={`sidebar-dropdown-item-new ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              const updatedTags = isSelected
                                ? newTaskData.tags.filter(t => t !== tag)
                                : [...newTaskData.tags, tag];
                              setNewTaskData({ ...newTaskData, tags: updatedTags });
                            }}
                          >
                            <span className={`tag-color-indicator ${tag.toLowerCase()}`} style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: tag === 'Bug' ? '#ef4444' : tag === 'Feature' ? '#3b82f6' : tag === 'Design' ? '#a855f7' : '#10b981',
                              flexShrink: 0
                            }} />
                            <span>{tag}</span>
                            {isSelected && <span className="dropdown-item-check-new">✓</span>}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Status</label>
                <div className={`sidebar-select-wrapper-custom-new ${activeDropdown === 'status' ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="sidebar-select-trigger-new"
                    onClick={() => setActiveDropdown(activeDropdown === 'status' ? null : 'status')}
                  >
                    <span>
                      {columns.find(col => col.id === (newTaskData.status || showAddForm))?.title || 'Select status'}
                    </span>
                    <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {activeDropdown === 'status' && (
                    <ul className="sidebar-dropdown-menu-new">
                      {columns.map(col => {
                        const currentVal = newTaskData.status || showAddForm;
                        const isSelected = currentVal === col.id;
                        return (
                          <li
                            key={col.id}
                            className={`sidebar-dropdown-item-new ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              setNewTaskData({ ...newTaskData, status: col.id });
                              setActiveDropdown(null);
                            }}
                          >
                            <span>{col.title}</span>
                            {isSelected && <span className="dropdown-item-check-new">✓</span>}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
              <div className="extra-sidebar-fields-new">
                  {/* Due Date */}
                  <div className="form-group-sidebar-new">
                    <label className="sidebar-label-new">Due Date</label>
                    <div className="sidebar-select-wrapper-new">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" className="sidebar-icon-new">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <input
                        type="date"
                        value={newTaskData.dueDate}
                        onChange={(e) => setNewTaskData({ ...newTaskData, dueDate: e.target.value })}
                        className="sidebar-select-new has-icon-new date-picker-new"
                      />
                    </div>
                  </div>

                  {/* Start Date */}
                  <div className="form-group-sidebar-new">
                    <label className="sidebar-label-new">Start Date</label>
                    <div className="sidebar-select-wrapper-new">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" className="sidebar-icon-new">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <input
                        type="date"
                        value={newTaskData.startDate}
                        onChange={(e) => setNewTaskData({ ...newTaskData, startDate: e.target.value })}
                        className="sidebar-select-new has-icon-new date-picker-new"
                      />
                    </div>
                  </div>

                  {/* Sprint */}
                  <div className="form-group-sidebar-new">
                    <label className="sidebar-label-new">Sprint</label>
                    <div className="sidebar-select-wrapper-new">
                      <select
                        value={newTaskData.sprint}
                        onChange={(e) => setNewTaskData({ ...newTaskData, sprint: e.target.value })}
                        className="sidebar-select-new"
                      >
                        <option value="">Select sprint</option>
                        <option value="Sprint 1">Sprint 1</option>
                        <option value="Sprint 2">Sprint 2</option>
                        <option value="Sprint 3">Sprint 3</option>
                      </select>
                    </div>
                  </div>

                  {/* Story Points */}
                  <div className="form-group-sidebar-new">
                    <label className="sidebar-label-new">Story Points</label>
                    <div className="story-points-input-wrapper-new">
                      <input
                        type="text"
                        placeholder="Enter story points"
                        value={newTaskData.storyPoints}
                        onChange={(e) => setNewTaskData({ ...newTaskData, storyPoints: e.target.value })}
                        className="story-points-input-new"
                      />
                      <span className="story-points-suffix-new">pts</span>
                    </div>
                  </div>

                  {/* Reporter */}
                  <div className="form-group-sidebar-new">
                    <label className="sidebar-label-new">Reporter</label>
                    <div className="sidebar-select-wrapper-new">
                      <div className="comment-avatar-new sidebar-avatar-new">AS</div>
                      <select
                        value={newTaskData.reporter}
                        onChange={(e) => setNewTaskData({ ...newTaskData, reporter: e.target.value })}
                        className="sidebar-select-new has-avatar-new"
                      >
                        <option value="Akash Sharma">Akash Sharma</option>
                        {orgMembers.map(member => (
                          <option key={member.id} value={member.name}>{member.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
            </div>
          </div>

          <div className="task-modal-footer">
            <div className="create-another-wrapper-new">
              <input
                type="checkbox"
                id="create-another-checkbox"
                checked={createAnother}
                onChange={(e) => setCreateAnother(e.target.checked)}
                className="create-another-checkbox-new"
              />
              <label htmlFor="create-another-checkbox" className="create-another-label-new">Create another task</label>
            </div>
            <button
              type="button"
              className="submit-btn"
              onClick={() => handleAddTask(showAddForm)}
            >
              Create Task
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancelAddTask}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div className="image-zoom-modal">
          <div className="image-zoom-content">
            <button
              className="image-zoom-close"
              onClick={() => setZoomedImage(null)}
            >
              ×
            </button>
            <img
              src={zoomedImage}
              alt="Zoomed view"
            />
          </div>
        </div>
      )}

      {/* ── Move-to-Completed confirmation modal ──────────── */}
      {pendingDrop && (
        <div className="task-backdrop-overlay" onClick={() => setPendingDrop(null)}>
          <div className="task-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="task-confirm-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <h3 className="task-confirm-title">Mark as Complete?</h3>
            <p className="task-confirm-body">
              Move <strong>"{pendingDrop.task.title}"</strong> to{' '}
              <strong>{columns.find(c => c.id === pendingDrop.targetColumnId)?.title}</strong>?
              This marks the task as done.
            </p>
            <div className="task-confirm-actions">
              <button className="task-confirm-cancel" onClick={() => setPendingDrop(null)}>Cancel</button>
              <button className="task-confirm-ok" onClick={() => {
                const { task, sourceColumnId, targetColumnId, dropIndex } = pendingDrop;
                setPendingDrop(null);
                executeDrop(task, sourceColumnId, targetColumnId, dropIndex);
                addHistoryEntry(`Moved to "${columns.find(c => c.id === targetColumnId)?.title}"`, task.id);
              }}>Move to Completed</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskBoard;
